import { Checkbox, makeStyles, mergeClasses, useAnnounce } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { DataManager } from '@syncfusion/ej2-data';
import { PredicateModel } from '@syncfusion/ej2-grids';
import { ClickEventArgs, ItemModel } from '@syncfusion/ej2-navigations';
import {
    CheckBoxChangeEventArgs,
    ColumnModel,
    FilterSettingsModel,
    GridActionEventArgs,
    GridComponent,
    PageSettingsModel,
    QueryCellInfoEventArgs,
    RowDataBoundEventArgs,
    RowDeselectEventArgs,
    RowSelectEventArgs,
    SearchSettingsModel,
    SelectionSettingsModel,
    SortDirection,
    SortEventArgs,
    SortSettingsModel,
    ValueType,
} from '@syncfusion/ej2-react-grids';
import { useQueryClient } from '@tanstack/react-query';
import { camelCase } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSessionStorageState from 'use-session-storage-state';
import { useIsFeatureEnabled } from '../../../api/features/features';
import {
    useGetAllFolders,
    useGetCustomGroups,
    useGetCustomRanges,
    useGetLatestExecutionInformation,
    useGetTaxReturnById,
    useGetTaxReturns,
    useGetTaxReturnsNameList,
    useSetTaxReturnStatus,
    useUpdateTaxReturns,
} from '../../../api/report/report';
import { applicationInsightsReactPlugin } from '../../../ApplicationInsightsService';
import { GridStateContextProvider } from '../../../contexts/gridStateContext';
import { ReturnDetailsContextProvider } from '../../../contexts/returnDetails/returnDetailsContext';
import { TaxReturnCalculationProvider } from '../../../contexts/taxReturnCalculationContext';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { ISelectedTaxReturnsAndFilters } from '../../../data-types/exportReports';
import { IFilterItem } from '../../../data-types/filterItem';
import { IExecutionStatusStorage } from '../../../data-types/IExecutionStatusStorage';
import { useReportsTableFiltering } from '../../../hooks/reportsTable/useReportsTableFiltering';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useHighlightSearchedValues } from '../../../hooks/useHighlightSearchedValues';
import { usePerformanceMetricsForTable } from '../../../hooks/usePerformanceMetricsForTable';
import { usePersistFocusInTable } from '../../../hooks/usePersistFocusInTable';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import {
    CalculationExecutionStatus,
    CalculationExecutionStatusCode,
    Report,
    ReportListRequest,
    ReportStatus,
    ReturnFolderDto,
    TaxReturnSaveRecordsRequest,
} from '../../../model';
import { REDIRECTED_ACTION_NAME, REDIRECTED_EVENT_NAME } from '../../../telemetry/events';
import { useEventTracker } from '../../../telemetry/hooks/useEventTracker';
import { DATA_ATTR_ANALYTICS_BUTTON_NAME } from '../../../telemetry/hooks/useGlobalClickTracker';
import { useUtmTracking } from '../../../telemetry/hooks/useUtmTracking';
import { cometDarkTheme, customPalette } from '../../../theme';
import { isResponseArrayValid } from '../../../utils/ApiClientUtils';
import { REPORTS_TABLE_HIDDEN_COLUMNS_NAME } from '../../../utils/BrowserStorageKeys.constants';
import { getSortComparer } from '../../../utils/ColumnUtils';
import { getDialogViewportPosition, IDialogAnchorRect, toOffsetParentCoords } from '../../../utils/filterDialogPosition';
import { getSelectedTaxReturnsAndFilters } from '../../../utils/FilterUtils';
import {
    adaptReportToShowableTaxReturnItemRecord,
    getReportPropertyValueByFieldName,
    getReportTableRows,
    getReturnRequest,
    ReportTableRow,
} from '../../../utils/ReportUtils';
import {
    cancelScheduledToolbarFlushRaf,
    createInitialToolbarFlushRafHandle,
    getSyncfusionToolbarModule,
    scheduleToolbarFlushRaf,
} from '../../../utils/SyncfusionGridToolbarUtils';
import { scrollToTop } from '../../../utils/TableUtils';
import AddReturnPanel, { ShowableTaxReturnItemRecord } from '../../add-return/AddReturnPanel';
import ConfirmReturnDeletionModal from '../../common/ConfirmReturnDeletionModal';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import LoadingSpinner from '../../common/LoadingSpinner';
import { ReportStatusButton } from '../../common/ReportStatusButton';
import EditReturnModal from '../../edit-return/EditReturnModal';
import BrowseReportNameCell from './BrowseReportNameCell';
import { IBulkEditBrowseViewSnapshot, useBulkEditViewTransition } from './bulk-edit/useBulkEditViewTransition';
import BulkEditReportNameCell from './BulkEditReportNameCell';
import FilterBar from './FilterBar';
import LastExecutedReportLink from './LastExecutedReportLink';
import ReportsBrowseGrid from './ReportsBrowseGrid';
import ReportsBulkEditGrid from './ReportsBulkEditGrid';
import {
    BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID,
    ReportsGridDialogGlobalStyles,
    REPORTS_GRID_ID,
    REPORTS_GRID_POPUP_ID,
} from './reportsGridDialogStyles';
import { IReportsTableColumnsProps, IReportsTableTemplates, REPORT_NAME_CELL_FOCUS_CLASS } from './ReportsTableColumns';

const sortingOptions: SortSettingsModel = {
    columns: [{ field: 'name', direction: 'Ascending' }],
};

const cloneSortColumns = (columns: SortSettingsModel['columns'] | object[]): { field: string; direction: SortDirection }[] =>
    (columns ?? []).flatMap((column) => {
        const sortColumn = column as { field?: string; direction?: SortDirection };
        return sortColumn.field && sortColumn.direction ? [{ field: sortColumn.field, direction: sortColumn.direction }] : [];
    });

const areSortColumnsEqual = (
    left: SortSettingsModel['columns'] | object[],
    right: { field: string; direction: SortDirection }[]
): boolean => {
    const normalizedLeft = cloneSortColumns(left);
    return (
        normalizedLeft.length === right.length &&
        normalizedLeft.every((column, index) => column.field === right[index]?.field && column.direction === right[index]?.direction)
    );
};

// Header sort icons are not derived from sortSettings on every paint. Clearing them here drops an
// indicator for a column that is no longer in the restored sort list.
const clearHeaderSortIndicators = (grid: GridComponent) => {
    grid.element?.querySelectorAll('th.e-headercell').forEach((header) => {
        header.setAttribute('aria-sort', 'none');
        header.querySelector('.e-sortnumber')?.remove();
        header.querySelector('.e-sortfilterdiv')?.classList.remove('e-ascending', 'e-descending', 'e-icon-ascending', 'e-icon-descending');
    });
};
const pageSettings: PageSettingsModel = { pageSize: 50 };
const numberOfColumns = 18;
export const EMPTY_CONTEXT_KEY_VALUE = 0;

export const ADD_NEW_RETURN_TOOLBAR_ITEM_ID = 'addNewReturn';
export const CLEAR_FILTERS_TOOLBAR_ITEM_ID = 'clearFilters';
export const RESET_GRID_TOOLBAR_ITEM_ID = 'resetGrid';
export const BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID = 'bulkEditReturns';
export const BULK_EDIT_SAVE_TOOLBAR_ITEM_ID = 'bulkEditSave';
/** Click-tracking name the Add New button carried before it became a native Syncfusion toolbar item. */
export const ADD_NEW_RETURN_ANALYTICS_NAME = 'add_new_calculation_button';

// Browse remount after Cancel uses a CSS transition rather than a keyframe: virtualization recycles
// <tr> elements, so a keyframe's "from" state can flash stale content. Toggle a hidden class
// synchronously in rowDataBound, then reveal on the next frame.
const ROW_LEAVE_DURATION_MS = 320;
const ROW_ENTER_DURATION_MS = 550;

// Griffel class names are space-separated atomic tokens; classList.add rejects a multi-token string.
const addClassNames = (element: Element, ...classNames: string[]) => {
    classNames.forEach((className) =>
        className
            .split(' ')
            .filter(Boolean)
            .forEach((token) => element.classList.add(token))
    );
};
const removeClassNames = (element: Element, ...classNames: string[]) => {
    classNames.forEach((className) =>
        className
            .split(' ')
            .filter(Boolean)
            .forEach((token) => element.classList.remove(token))
    );
};

const DEFAULT_VISIBLE_COLUMN_FIELDS = [
    'name',
    'status',
    'entityCode',
    'entityName',
    'jurisdiction',
    'period',
    'case',
    'executed',
    'userFullName',
];
export const DEFAULT_HIDDEN_COLUMN_FIELDS = [
    'customGroupName',
    'description',
    'ignoreEntityGroup',
    'includeAllBU',
    'customRangeName',
    'postAmountOption',
    'postAmountGroupName',
];
const RESETTABLE_COLUMN_FIELDS = [...DEFAULT_VISIBLE_COLUMN_FIELDS, ...DEFAULT_HIDDEN_COLUMN_FIELDS];

// Mirrors each resettable ColumnDirective's declared `width` below -- Reset Grid uses this to also undo any
// manual column resizing, not just visibility, so it matches the column widths of a freshly loaded page.
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
    name: 420,
    description: 250,
    status: 170,
    entityCode: 125,
    entityName: 110,
    jurisdiction: 125,
    period: 110,
    case: 102,
    executed: 140,
    userFullName: 150,
    customGroupName: 150,
    ignoreEntityGroup: 130,
    includeAllBU: 130,
    customRangeName: 150,
    postAmountOption: 220,
    postAmountGroupName: 150,
};

const areColumnFieldSetsEqual = (a: string[], b: string[]): boolean => a.length === b.length && a.every((field) => b.includes(field));

/**
 * Finds an element inside whichever of the two homes the grid's dialogs currently occupy -- inside the
 * grid, or inside the wrapper Syncfusion reparents them into (see REPORTS_GRID_POPUP_ID). Only one dialog
 * is ever open, so the first match in document order is the right one.
 */
export const getReportsGridDialogElement = <T extends HTMLElement>(selector: string): T | null =>
    document.querySelector<T>(`#${REPORTS_GRID_ID} ${selector}, #${REPORTS_GRID_POPUP_ID} ${selector}`);

/**
 * Positions a grid dialog under the control that opened it.
 *
 * Syncfusion writes its own `top`/`left` before deciding whether to reparent the dialog onto
 * document.body -- and does not recompute them afterwards, so the grid-relative offset ends up applied on
 * top of the wrapper's own grid-relative offset and the dialog lands roughly twice as far down and right
 * as intended. Recomputing here from the anchor's viewport rect, then converting into whichever offset
 * parent the dialog currently has, makes the result correct in either home.
 */
// Exported for testing: a regression guard on the max-height priority below relies on calling this directly.
export const positionGridDialog = (dialog: HTMLElement, anchor: HTMLElement) => {
    const anchorBounds = anchor.getBoundingClientRect();
    const dialogBounds = dialog.getBoundingClientRect();
    const anchorRect: IDialogAnchorRect = {
        topPx: anchorBounds.top,
        leftPx: anchorBounds.left,
        rightPx: anchorBounds.right,
        bottomPx: anchorBounds.bottom,
        widthPx: anchorBounds.width,
    };
    const position = getDialogViewportPosition(
        anchorRect,
        { widthPx: dialogBounds.width, heightPx: dialogBounds.height },
        {
            // clientWidth/clientHeight rather than window.innerWidth/innerHeight: the window figures
            // include the scrollbar, which would eat into the viewport margin.
            widthPx: document.documentElement.clientWidth,
            heightPx: document.documentElement.clientHeight,
        }
    );

    // offsetParent is null for a fixed-position or detached element; the viewport origin is the right
    // fallback in both cases.
    const offsetParent = dialog.offsetParent as HTMLElement | null;
    const offsetParentBounds = offsetParent?.getBoundingClientRect() ?? { top: 0, left: 0 };
    const { topPx, leftPx } = toOffsetParentCoords(position, offsetParentBounds);

    dialog.style.top = `${topPx}px`;
    dialog.style.left = `${leftPx}px`;
    // setProperty(..., 'important') rather than a plain assignment: reportsGridDialogStyles.tsx has its own
    // `max-height: calc(100vh - 16px) !important` fallback rule on these dialogs (see the comment there), and
    // a plain inline assignment always loses to ANY stylesheet rule with !important, regardless of specificity.
    // This value is more precise -- it accounts for the dialog's actual reparented position, not just viewport
    // height -- so it must be the one that wins once this function has run.
    dialog.style.setProperty('max-height', `${position.maxHeightPx}px`, 'important');
    dialog.setAttribute('data-filter-dialog-anchor', position.anchor === 'right' ? 'right' : 'left');
};

const focusOnLinkStyles = {
    textDecoration: 'underline',
    '& span': {
        color: customPalette.themePrimary,
    },
};

const useReportsTableStyles = makeStyles({
    disabledCheckboxIndicator: {
        borderRadius: '6px',
    },
    // Approximates Fluent 8 AnimationClassNames.fadeIn200 for toolbar item-set changes.
    toolbarFadeIn: {
        animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
        animationDuration: '267ms',
        animationTimingFunction: 'cubic-bezier(.1,.25,.75,.9)',
    },
    rowLeaveTransition: {
        transition: `opacity ${ROW_LEAVE_DURATION_MS}ms cubic-bezier(.1,.9,.2,1), transform ${ROW_LEAVE_DURATION_MS}ms cubic-bezier(.1,.9,.2,1)`,
    },
    rowEnterTransition: {
        transition: `opacity ${ROW_ENTER_DURATION_MS}ms cubic-bezier(.1,.9,.2,1), transform ${ROW_ENTER_DURATION_MS}ms cubic-bezier(.1,.9,.2,1)`,
    },
    rowHidden: {
        opacity: 0,
        transform: 'translate3d(0, 8px, 0)',
    },
    wrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)',
    },
    wrapperWithActiveMessage: {
        height: 'calc(100vh - 152px)',
    },
    filterBar: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
    scrollablePaneWrapper: {
        // Unlike loadingSpinnerContainer below (its sibling/alternative under the same flex column
        // wrapper), this had no explicit height -- it sized to its own content instead of filling the
        // wrapper's fixed height, so switching to edit view (fewer rows: only the selected ones) visibly
        // shrank the whole grid area instead of just showing fewer rows within the same-height box.
        height: '100%',
        position: 'relative',
        backgroundColor: 'var(--colorNeutralBackground1)',
        border: '1px solid var(--colorNeutralStroke2)',
        borderRadius: 'var(--borderRadiusMedium)',
        boxShadow: 'none',
        overflow: 'auto',
        margin: '12px 15px 0',
        [`& .${REPORT_NAME_CELL_FOCUS_CLASS}:focus`]: {
            ...focusOnLinkStyles,
        },
    },
    // While unselected rows fade out, the browse host stays mounted. Lock the whole pane so
    // header/select-all/toolbar cannot be used — no JS click swallowing.
    swapLocked: {
        pointerEvents: 'none',
        userSelect: 'none',
    },
    // Edit host has no selection column / name links; suppress the browse link-focus chrome on name cells.
    bulkEditActive: {
        [`& .${REPORT_NAME_CELL_FOCUS_CLASS}:focus`]: {
            textDecoration: 'none',
            '& span': {
                color: customPalette.blueDark,
            },
        },
    },
    loadingSpinnerContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reportNameCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        width: '100%',
    },
    disabledItem: {
        color: cometDarkTheme.colorNeutralStrokeDisabled,
    },
});

const ReportsTable = () => {
    const { trackScrollStarted, trackScrollFinished, trackRenderFinished } = usePerformanceMetricsForTable('List of returns');
    const { t } = useTranslation();
    const { functionalAccess } = useFunctionalAccess();
    const { activeMessage } = useFeedbackMessages();
    const queryClient = useQueryClient();

    const styles = useReportsTableStyles();
    const wrapperStyles = mergeClasses(styles.wrapper, activeMessage && styles.wrapperWithActiveMessage);
    const {
        filterBar: filterBarStyles,
        scrollablePaneWrapper: scrollablePaneWrapperStyles,
        bulkEditActive,
        loadingSpinnerContainer,
    } = styles;

    const { selectedFolderFilter, clearAllFilters } = useReportsTableFiltering();

    const [folders, setFolders] = useState<IFilterItem[]>([]);
    const [hiddenColumnFields, setHiddenColumnFields] = useSessionStorageState<string[]>(REPORTS_TABLE_HIDDEN_COLUMNS_NAME, {
        defaultValue: DEFAULT_HIDDEN_COLUMN_FIELDS,
    });
    const [hasHiddenResettableColumns, setHasHiddenResettableColumns] = useState(
        !areColumnFieldSetsEqual(hiddenColumnFields, DEFAULT_HIDDEN_COLUMN_FIELDS)
    );
    const [hasActiveColumnFilters, setHasActiveColumnFilters] = useState(false);
    const [disableActionButtons, setDisableActionButtons] = useState<boolean>(true);
    // Grid checkbox selection combined with virtualization is unreliable: getSelectedRecords() can
    // return stale/empty results once selected rows scroll out of the virtualized viewport. Track the
    // selected ids ourselves from the rowSelected/rowDeselected/checkBoxChange events (and resolve
    // select-all against the full filtered data set) so bulk edit, export, and toolbar state always
    // see the same selection.
    const selectedRowIdsRef = useRef<Set<number>>(new Set());
    const stopFocusReclaimRef = useRef<(() => void) | undefined>(undefined);
    useEffect(() => () => stopFocusReclaimRef.current?.(), []);
    const bulkEditSession = useBulkEditViewTransition();
    const pendingBrowseViewSnapshotRef = useRef<IBulkEditBrowseViewSnapshot | null>(null);
    const focusBulkEditOnBrowseCreateRef = useRef(false);
    const enterBulkEditRafRef = useRef<number | null>(null);
    // Selection must NOT flow through toolbarItems → grid.toolbar + dataBind(): that rebinds the whole
    // grid (and restarts the toolbar fade) on every checkbox click. Enable/disable + Edit/Bulk-edit label
    // are flushed imperatively via toolbarModule.enableItems / DOM text instead (same enableItems pattern
    // as RowTableFormViewer / ColumnTableFormViewer).
    const bulkEditToolbarFlushRafRef = useRef(createInitialToolbarFlushRafHandle());
    useEffect(() => () => cancelScheduledToolbarFlushRaf(bulkEditToolbarFlushRafRef), []);
    useEffect(
        () => () => {
            if (enterBulkEditRafRef.current != null) {
                window.cancelAnimationFrame(enterBulkEditRafRef.current);
                enterBulkEditRafRef.current = null;
            }
        },
        []
    );
    const { focusOnSelectedCell, setSelectedCellIndex, selectedCellIndex } = usePersistFocusInTable();
    const sortingDirection = useRef<SortDirection | undefined>('Ascending');
    const [hasLatestCalculationStatus, { setTrue: setHasLatestCalculationStatusTrue }] = useBoolean(false);
    const { registerGlobalDependency, fetchAllCalculationExecutionStatuses, removeTaxReturnCalculationReport } =
        useTaxReturnCalculationState();
    const { mutateAsync: getLatestExecutionInformation } = useGetLatestExecutionInformation();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { mutateAsync: updateTaxReturns } = useUpdateTaxReturns();
    const [defaultSortingEnabled, { setFalse: disableDefaultSorting }] = useBoolean(true);
    const { removeReturn } = useUserSettings();

    const [isAddReturnPanelOpen, { setTrue: openAddReturnPanel, setFalse: closeAddReturnPanel }] = useBoolean(false);
    const [isEditReturnModalOpen, { setTrue: openEditReturnModal, setFalse: closeEditReturnModal }] = useBoolean(false);
    const [isDeleteReturnModalOpen, { setTrue: openDeleteReturnModal, setFalse: closeDeleteReturnModal }] = useBoolean(false);

    const [returnToDelete, setReturnToDelete] = useState<number | undefined>(undefined);
    const { announce } = useAnnounce();

    const [selectedReturn, setSelectedReturn] = useState<ShowableTaxReturnItemRecord | undefined>(undefined);
    const { data: allReturnsNames, queryKey: getTaxReturnsNameListQueryKey } = useGetTaxReturnsNameList({
        reportId: selectedReturn?.taxReturnItemKey,
    });
    const { refetch: refetchReport } = useGetTaxReturnById<Report>(selectedReturn?.taxReturnItemKey ?? 0, { query: { enabled: false } });

    const { data: isCreateNewReturnEnabled } = useIsFeatureEnabled({
        featureFlagName: 'CreateNewReturnEnabled',
    });
    const { data: isEditCalculationEnhancementsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'EditCalculationEnhancementsEnabled',
    });
    const [recentlyCreatedTaxReturnItemKeys, setRecentlyCreatedTaxReturnItemKeys] = useState<number[] | undefined>(undefined);
    document.title = t('review');

    const name = 'name';
    const userFullName = 'userFullName';
    const entityFilterName = 'entityName';
    const entityCodeFilterName = 'entityCode';
    const jurisdictionFilterName = 'jurisdiction';
    const caseFilterName = 'case';

    const getExecutedTemplate = (data: Report) => {
        return <LastExecutedReportLink {...data} disabled={!data.canExecute} />;
    };

    const gridRef = useRef<GridComponent>(null);
    const headerCheckboxClickCleanupRef = useRef<(() => void) | null>(null);
    const handleHeaderCheckboxToggleRef = useRef<(event: Event) => void>(() => undefined);
    const selectAllActiveRef = useRef(false);
    useEffect(
        () => () => {
            headerCheckboxClickCleanupRef.current?.();
            headerCheckboxClickCleanupRef.current = null;
        },
        []
    );

    const getTaxReturnsRequest = (): ReportListRequest => {
        return getReturnRequest(selectedFolderFilter?.key ? parseInt(selectedFolderFilter.key) : undefined);
    };

    const updateTaxReturnRowDataInPlace = (taxReturnItemKey: number, updatedInformation: any, refresh: boolean = false) => {
        const report = reportRows.find((report) => report.id === taxReturnItemKey);
        if (!report) {
            focusOnSelectedCell(false);
            return;
        }

        // The single-report endpoint doesn't compute canExecute/executeWarningMessage, so never let it overwrite the list values.
        const mergedReport = {
            ...report,
            ...updatedInformation,
            canExecute: report.canExecute,
            executeWarningMessage: report.executeWarningMessage,
            id: taxReturnItemKey,
        };
        const derivedReport = getReportTableRows([mergedReport], customGroups, customRanges, t)[0] ?? mergedReport;
        dataManager.update('id', derivedReport);

        if (refresh) {
            gridRef.current?.refresh();
        } else {
            gridRef.current?.setRowData(taxReturnItemKey, derivedReport);
        }

        focusOnSelectedCell(false);
    };

    const deleteTaxReturnRowDataInPlace = (taxReturnItemKey: number) => {
        gridRef.current?.deleteRecord('id', taxReturnItemKey);
        removeTaxReturnCalculationReport(taxReturnItemKey);
        removeReturn(taxReturnItemKey);
    };

    const updateReturnLastExecutedDateInList = (
        taxReturnItemKey: number,
        lastExecuted: string,
        lastExecutedBy?: string,
        refresh: boolean = false
    ) => {
        const valuesToUpdate: any = { executed: lastExecuted };

        if (lastExecutedBy) {
            valuesToUpdate[userFullName] = lastExecutedBy;
        }

        updateTaxReturnRowDataInPlace(taxReturnItemKey, valuesToUpdate, refresh);
    };

    const { mutateAsync, isLoading: isLoadingSetStatus } = useSetTaxReturnStatus({
        mutation: {
            onSuccess: async (data: any, { reportId, params }) => {
                if (data) {
                    updateTaxReturnRowDataInPlace(reportId, { status: params?.reportStatus }, false);
                }
            },
        },
    });

    useEffect(() => {
        if (isLoadingSetStatus) {
            gridRef.current?.showSpinner();
        } else {
            gridRef.current?.hideSpinner();
        }
    }, [isLoadingSetStatus]);

    const { data: folderItems, refetch: updateFolderList } = useGetAllFolders();
    const { getUtmParams, cleanUtmParamsFromUrl } = useUtmTracking();
    const { trackEvent, isReady } = useEventTracker();
    const { data: filteredReportsResponse, isError, isLoading, mutateAsync: getTaxReturns } = useGetTaxReturns();
    const { data: customGroups } = useGetCustomGroups();
    const { data: customRanges } = useGetCustomRanges();

    const reportRows = useMemo(
        () => getReportTableRows(filteredReportsResponse?.reports, customGroups, customRanges, t),
        [filteredReportsResponse, customGroups, customRanges, t]
    );
    const dataManager = useMemo(() => new DataManager(reportRows), [reportRows]);
    const contentLength = filteredReportsResponse?.reports?.length ?? 0;

    useLayoutEffect(() => {
        if (!bulkEditSession.isActive) {
            const snapshot = bulkEditSession.takeEntryViewSnapshot();
            if (snapshot) {
                pendingBrowseViewSnapshotRef.current = snapshot;
                focusBulkEditOnBrowseCreateRef.current = true;
            }
        }
    }, [bulkEditSession.isActive]);

    const memoizedFilters = useMemo(() => getTaxReturnsRequest(), [selectedFolderFilter]);

    const toolbarItems: (string | ItemModel)[] = useMemo(() => {
        const resetGridItem: ItemModel | undefined = hasHiddenResettableColumns
            ? {
                  text: t('resetGrid').toString(),
                  tooltipText: t('resetGrid').toString(),
                  id: RESET_GRID_TOOLBAR_ITEM_ID,
                  prefixIcon: 'e-icons e-reset',
                  align: 'Right',
              }
            : undefined;

        if (bulkEditSession.isActive) {
            // No field editing on this shell yet, so there are never pending changes. Flip this (or replace
            // with a hasOverrides check) once Set-for-all / per-row edits land. Syncfusion's ItemModel.disabled
            // only adds .e-overlay on the wrapper (aria-disabled stays false); StyledTable keys primary-disabled
            // styles off that class so Save matches Fluent's primary disabled look.
            const hasPendingBulkEditChanges = false;
            const editViewItems: (string | ItemModel)[] = [
                {
                    text: t('save').toString(),
                    tooltipText: t('save').toString(),
                    id: BULK_EDIT_SAVE_TOOLBAR_ITEM_ID,
                    prefixIcon: 'e-icons e-save',
                    disabled: !hasPendingBulkEditChanges,
                },
                {
                    text: t('cancel').toString(),
                    tooltipText: t('cancel').toString(),
                    id: BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID,
                    prefixIcon: 'e-icons e-close',
                },
            ];
            if (resetGridItem) {
                editViewItems.push(resetGridItem);
            }
            // Column Chooser reaches Set-for-all fields whose columns are hidden by default.
            editViewItems.push('ColumnChooser');
            return editViewItems;
        }
        const items: (string | ItemModel)[] = [];
        if (isCreateNewReturnEnabled) {
            items.push({
                text: t('add').toString(),
                tooltipText: t('add').toString(),
                id: ADD_NEW_RETURN_TOOLBAR_ITEM_ID,
                prefixIcon: 'e-icons e-add',
                disabled: !functionalAccess?.createReportsButtonEnabled,
            });
        }
        items.push({
            // Initial label only -- selection-driven Edit vs Bulk edit text is applied in
            // flushBulkEditToolbarItemState so checkbox clicks do not rebuild toolbarItems.
            text: t('edit').toString(),
            tooltipText: t('edit').toString(),
            id: BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID,
            prefixIcon: 'e-icons e-edit',
            // Initial disabled only -- real enable state is flushed via enableItems (see above).
            disabled: true,
        });
        if (hasActiveColumnFilters) {
            items.push({
                text: t('clearFilters').toString(),
                tooltipText: t('clearFilters').toString(),
                id: CLEAR_FILTERS_TOOLBAR_ITEM_ID,
                prefixIcon: 'e-icons e-close',
                align: 'Right',
            });
        }
        if (resetGridItem) {
            items.push(resetGridItem);
        }
        items.push('ColumnChooser', 'Search');
        return items;
    }, [
        isCreateNewReturnEnabled,
        functionalAccess?.createReportsButtonEnabled,
        hasActiveColumnFilters,
        hasHiddenResettableColumns,
        bulkEditSession.isActive,
        t,
    ]);

    // The Syncfusion grid only reads its `toolbar` prop on initial render -- toggling the Reset Grid item's
    // presence afterward (based on `hasHiddenResettableColumns`) requires imperatively re-assigning the
    // property and calling dataBind() to make the grid pick up the change. Intentionally NOT driven by
    // selection: that used to rebind + fade the toolbar on every checkbox click.
    useEffect(() => {
        const grid = gridRef.current;
        if (grid) {
            grid.toolbar = toolbarItems;
            grid.dataBind();

            // Cross-fade the toolbar in on every item-set change (e.g. entering/leaving bulk edit view)
            // rather than letting the new buttons just snap into place. Syncfusion may reuse the same
            // `.e-toolbar` element across rebinds, so the fade-in class is removed and re-added with a
            // forced reflow in between to restart the CSS animation even when it was already present.
            const toolbarElement = grid.element.querySelector<HTMLElement>('.e-toolbar');
            if (toolbarElement) {
                removeClassNames(toolbarElement, styles.toolbarFadeIn);
                void toolbarElement.offsetWidth;
                addClassNames(toolbarElement, styles.toolbarFadeIn);
            }
        }
        scheduleBulkEditToolbarItemStateUpdate();
    }, [toolbarItems, styles.toolbarFadeIn]);

    // Set when the Column Chooser toolbar button is clicked while an Excel filter dialog is still open (see
    // the guard inside the effect below). Both dialogs can end up reparented into the exact same wrapper
    // element by Syncfusion's own resetDialogAppend (ej2-grids), keyed only by the grid's own id -- and
    // closing the Excel filter unconditionally removes that entire wrapper from the DOM as part of its own
    // cleanup (CheckBoxFilterBase.prototype.destroy). The guard below now rescues the Column Chooser out of
    // that wrapper before triggering that close (see rescueColumnChooserFromSharedWrapper), so it is never
    // actually living there when the removal happens -- but the deferred open here is still needed
    // regardless of that rescue: the Column Chooser's own real open must only run after the filter's close
    // has genuinely finished, not race it.
    const pendingColumnChooserOpenRef = useRef<{ x: number; y: number; target: HTMLElement } | null>(null);
    // Defensive-only backstop for the deferred open above -- cleared as soon as the MutationObserver in the
    // effect below actually confirms the Excel filter's dialog was removed. Reading the ej2-grids source
    // (checkbox-filter-base.js), that removal happens synchronously with no CSS transition to wait out
    // (`animationSettings: { effect: 'None' }` on the filter dialog itself), so this should never fire in
    // practice; it only guards against an unexpected future Syncfusion change leaving the request stranded.
    const pendingColumnChooserOpenFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Syncfusion's Excel filter has no public "dialog opened" event, so the Entity/Entity Groups tab strip
    // above its search box is injected by watching for the filter dialog's own DOM node appearing. The dialog
    // is torn down and rebuilt fresh every time it's opened, so this has to re-run per open rather than once
    // -- and the grid itself remounts on folder changes (`key={tableKey}` below), so the observer is
    // re-attached whenever the folder filter or loaded data changes.
    //
    // Both the Entity and Entity code columns get this same tab strip. Each checkbox row is tagged with
    // `data-entity-group` via plain DOM manipulation rather than a Syncfusion `filter.itemTemplate`.
    useEffect(() => {
        const grid = gridRef.current;
        const gridElement = grid?.element;
        if (!grid || !gridElement) {
            return;
        }

        type EntityFilterTabKey = 'entity' | 'group';
        type EntityGroupFilterColumnConfig = {
            columnUid: string;
            isEntityGroupByKey: Map<string, boolean>;
        };

        const entityGroupFilterColumnsByUid = new Map<string, EntityGroupFilterColumnConfig>();
        (
            [
                { filterName: entityFilterName, getGroupKey: (report: Report) => report.entityName },
                { filterName: entityCodeFilterName, getGroupKey: (report: Report) => report.entityCode },
            ] satisfies { filterName: string; getGroupKey: (report: Report) => string | null | undefined }[]
        ).forEach(({ filterName, getGroupKey }) => {
            const columnUid = grid.getColumnByField(filterName)?.uid;
            if (!columnUid) {
                return;
            }
            const isEntityGroupByKey = new Map<string, boolean>();
            (filteredReportsResponse?.reports ?? []).forEach((report) => {
                const groupKey = getGroupKey(report);
                if (groupKey && !isEntityGroupByKey.has(groupKey)) {
                    isEntityGroupByKey.set(groupKey, !!report.isForEntityGroup);
                }
            });
            entityGroupFilterColumnsByUid.set(columnUid, { columnUid, isEntityGroupByKey });
        });

        if (entityGroupFilterColumnsByUid.size === 0) {
            return;
        }

        const injectEntityFilterTabs = (dialog: HTMLElement) => {
            const content = dialog.querySelector('.e-dlg-content');
            // Syncfusion sometimes reparents an already-open dialog into a different container (see
            // resetDialogAppend in ej2-grids' checkbox-filter-base), which fires a second "node added"
            // mutation for the same dialog element -- checked on `dialog` itself (the tab strip's actual
            // parent) rather than `content`, since the strip is inserted as `content`'s sibling below.
            if (!content || dialog.querySelector('.entity-filter-tabs')) {
                return;
            }

            dialog.setAttribute('data-entity-tab', 'entity');

            const tabStrip = document.createElement('div');
            tabStrip.className = 'entity-filter-tabs';
            tabStrip.setAttribute('role', 'tablist');
            tabStrip.setAttribute('aria-label', t('entityFilterTabs').toString());

            const tabKeys: EntityFilterTabKey[] = ['entity', 'group'];
            const selectTab = (tabKey: EntityFilterTabKey) => {
                dialog.setAttribute('data-entity-tab', tabKey);
                tabStrip.querySelectorAll<HTMLElement>('.entity-filter-tab').forEach((tab) => {
                    tab.setAttribute('aria-selected', String(tab.getAttribute('data-tab') === tabKey));
                });
            };

            const appendTab = (tabKey: EntityFilterTabKey, label: string) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'entity-filter-tab';
                button.textContent = label;
                button.setAttribute('data-tab', tabKey);
                button.setAttribute('role', 'tab');
                button.setAttribute('aria-selected', tabKey === 'entity' ? 'true' : 'false');
                // Both tabs stay in the Tab order; activation is click (or Enter/Space via native button behavior).
                button.tabIndex = 0;
                tabStrip.appendChild(button);
            };

            appendTab('entity', t('entity').toString());
            appendTab('group', t('entityGroups').toString());

            // One delegated listener for the strip instead of per-button handlers.
            tabStrip.addEventListener('click', (event) => {
                const tab = (event.target as HTMLElement).closest<HTMLElement>('.entity-filter-tab');
                const tabKey = tab?.getAttribute('data-tab') as EntityFilterTabKey | null;
                if (tabKey === 'entity' || tabKey === 'group') {
                    selectTab(tabKey);
                }
            });
            tabStrip.addEventListener('keydown', (event: KeyboardEvent) => {
                const tab = (event.target as HTMLElement).closest<HTMLElement>('.entity-filter-tab');
                const tabKey = tab?.getAttribute('data-tab') as EntityFilterTabKey | null;
                if (tabKey !== 'entity' && tabKey !== 'group') {
                    return;
                }
                const currentIndex = tabKeys.indexOf(tabKey);
                let nextIndex: number | null = null;
                if (event.key === 'ArrowRight') {
                    nextIndex = (currentIndex + 1) % tabKeys.length;
                } else if (event.key === 'ArrowLeft') {
                    nextIndex = (currentIndex - 1 + tabKeys.length) % tabKeys.length;
                } else if (event.key === 'Home') {
                    nextIndex = 0;
                } else if (event.key === 'End') {
                    nextIndex = tabKeys.length - 1;
                }
                if (nextIndex === null) {
                    return;
                }
                // Focus only -- do not activate (that would flip data-entity-tab / the visible checklist).
                // Stop Syncfusion's Excel-filter key handling from stealing focus out of the tab strip.
                event.preventDefault();
                event.stopPropagation();
                tabStrip.querySelectorAll<HTMLElement>('.entity-filter-tab')[nextIndex]?.focus();
            });

            // Inserted as a sibling before `.e-dlg-content` (not as a child of it) so this plain DOM node
            // never touches `.e-dlg-content`'s own children -- Syncfusion re-renders that subtree freely
            // while the dialog is open (search filtering, infinite scroll, etc.), and reparenting/reordering
            // nodes it owns invites the same kind of cleanup conflict itemTemplate did.
            dialog.insertBefore(tabStrip, content);
        };

        // Rows Syncfusion owns itself (Select All, the search box's "add current selection" row) either
        // won't match a key in isEntityGroupByKey or repeat a key already seen -- both cases leave the
        // row untagged, so it stays visible regardless of which tab is active.
        const tagFilterRow = (row: HTMLElement, config: EntityGroupFilterColumnConfig) => {
            const groupKey = row.querySelector('.e-checkboxfiltertext')?.textContent?.trim();
            if (!groupKey || !config.isEntityGroupByKey.has(groupKey) || row.hasAttribute('data-entity-group')) {
                return;
            }
            row.setAttribute('data-entity-group', config.isEntityGroupByKey.get(groupKey) ? 'true' : 'false');
        };

        // Syncfusion's Excel filter ArrowUp/Down walks every input/button in the dialog, including rows hidden by
        // `display: none` for the inactive entity tab -- focus then lands on an invisible checkbox and appears stuck.
        const getVisibleExcelFilterFocusables = (dialog: HTMLElement): HTMLElement[] =>
            Array.from(
                dialog.querySelectorAll<HTMLElement>(
                    'input, button, [tabindex]:not([tabindex="-1"]), .e-menu-item:not(.e-disabled):not(.e-separator)'
                )
            ).filter((element) => {
                const row = element.closest<HTMLElement>('.e-ftrchk');
                return !row || getComputedStyle(row).display !== 'none';
            });

        const navigateVisibleExcelFilterRows = (dialog: HTMLElement, key: 'ArrowUp' | 'ArrowDown') => {
            const focusables = getVisibleExcelFilterFocusables(dialog);
            if (focusables.length === 0) {
                return;
            }
            const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);
            const nextIndex =
                key === 'ArrowUp' ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1) : (currentIndex + 1) % focusables.length;
            const nextElement = focusables[nextIndex];
            nextElement.focus();
            const focusTarget = nextElement.classList.contains('e-chk-hidden')
                ? nextElement.closest<HTMLElement>('.e-ftrchk')
                : nextElement.closest<HTMLElement>('.e-menu-item') ?? nextElement;
            dialog.querySelector('.e-chkfocus')?.classList.remove('e-chkfocus');
            dialog.querySelector('.e-menufocus')?.classList.remove('e-menufocus');
            if (focusTarget?.classList.contains('e-ftrchk')) {
                focusTarget.classList.add('e-chkfocus');
            } else if (focusTarget?.classList.contains('e-menu-item')) {
                focusTarget.classList.add('e-menufocus');
            }
        };

        // Looked up fresh from `document` (not `gridElement`) every time, rather than caching a reference to
        // it -- Syncfusion can recreate its own internal grid DOM (e.g. on a browser zoom change, which
        // triggers its column-width recalculation) without any of this effect's dependencies changing, which
        // would otherwise leave a cached `gridElement` pointing at a detached, no-longer-rendered node.
        const getFilterIconForDialog = (dialog: HTMLElement): HTMLElement | null => {
            const columnUid = dialog.getAttribute('uid');
            return columnUid ? document.body.querySelector<HTMLElement>(`[e-mappinguid="e-flmenu-${columnUid}"]`) : null;
        };

        // Syncfusion doesn't mark the filter icon in any way while its own dialog is open (only once a
        // filter is actually applied, via "e-filtered") -- StyledTable.tsx renders "e-filter-menu-open" as
        // the same filled icon, so this just needs to track the dialog's open/closed lifecycle onto it.
        const markFilterIconOpenState = (dialog: HTMLElement, isOpen: boolean) => {
            getFilterIconForDialog(dialog)?.classList.toggle('e-filter-menu-open', isOpen);
        };

        // Row-tagging only matters while one of these columns' own Excel filter dialog is open -- gating it
        // on this (rather than tagging every `.e-ftrchk` node added anywhere) keeps the observer cheap while
        // virtual scrolling continuously inserts grid rows elsewhere in the document. Only one Excel filter
        // dialog can be open at a time, so a single config reference is enough to track it.
        let openEntityGroupFilterConfig: EntityGroupFilterColumnConfig | null = null;

        // Syncfusion's Select All checks every row in its checkbox model, including ones the active
        // entity/entity-group tab currently hides with `display: none` (tagFilterRow/StyledTable.tsx above).
        // Re-syncing on the next tick (after Syncfusion's own click handling has run) un-checks whichever
        // half is hidden by clicking its checkbox input directly -- the same path a real user click takes --
        // so Syncfusion's own model stays in sync with what's visible.
        const uncheckRowsHiddenByActiveTab = (dialog: HTMLElement) => {
            const activeTab = dialog.getAttribute('data-entity-tab');
            const hiddenTagValue = activeTab === 'group' ? 'false' : 'true';
            dialog.querySelectorAll<HTMLElement>(`.e-ftrchk[data-entity-group="${hiddenTagValue}"]`).forEach((row) => {
                const checkbox = row.querySelector<HTMLInputElement>('.e-frame');
                if (checkbox?.classList.contains('e-check')) {
                    checkbox.click();
                }
            });
        };
        const resyncSelectAllWithActiveTab = (event: MouseEvent) => {
            const selectAllRow = (event.target as HTMLElement)?.closest<HTMLElement>('.e-ftrchk')?.querySelector('.e-selectall');
            const dialog = (event.target as HTMLElement)?.closest<HTMLElement>('.e-excelfilter');
            const dialogUid = dialog?.getAttribute('uid');
            if (!selectAllRow || !dialog || !dialogUid || !entityGroupFilterColumnsByUid.has(dialogUid)) {
                return;
            }
            setTimeout(() => uncheckRowsHiddenByActiveTab(dialog), 0);
        };

        // Capture-phase keyup so we run before Syncfusion's Excel-filter keyUp, which otherwise focuses
        // interleaved rows hidden by the inactive entity tab.
        const navigateEntityFilterVisibleRows = (event: KeyboardEvent) => {
            if ((event.key !== 'ArrowUp' && event.key !== 'ArrowDown') || event.altKey) {
                return;
            }
            const dialog = (event.target as HTMLElement)?.closest<HTMLElement>('.e-excelfilter');
            if (!dialog?.hasAttribute('data-entity-tab')) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            navigateVisibleExcelFilterRows(dialog, event.key);
        };

        // Syncfusion's own reparent (resetDialogAppend, referenced above) is a plain `appendChild` of the
        // already-open dialog onto document.body -- and moving a focused element with appendChild/
        // insertBefore drops browser focus onto document.body in every current browser, not just this test
        // environment (the same well-documented behavior the new `moveBefore()` API exists to avoid; see
        // e.g. Mozilla bug 481468 and Chromium's own explainer for it). Whatever had focus inside the dialog
        // at that moment -- Syncfusion's own auto-focused search box, or a keyboard user mid-Tab -- silently
        // loses it, and the next Tab press resumes from the top of the document instead of from the dialog.
        // Only acting when focus has actually landed on document.body (rather than unconditionally
        // refocusing whenever this mutation fires) keeps this from fighting Syncfusion's own focus handling
        // during a normal, non-reparented open, where this same branch also runs but activeElement is never
        // document.body at that point.
        const restoreDialogFocusIfLostToReparent = (dialog: HTMLElement, searchInputSelector: string) => {
            if (document.activeElement !== document.body) {
                return;
            }
            dialog.querySelector<HTMLElement>(searchInputSelector)?.focus();
        };

        // Rescues the Column Chooser's dialog element out of the shared `#<gridId>_e-popup` wrapper any time
        // an Excel filter dialog is added to the DOM (called from the observer's addedNodes handling below) --
        // i.e. every time a column's Excel filter opens, regardless of which UI action opened it. This is the
        // general fix for the collision this whole effect exists to guard against: both dialogs can be
        // reparented into that exact same wrapper by Syncfusion's own height-gated resetDialogAppend
        // (ej2-grids' util.js, invoked from both column-chooser.js's renderColumnChooser and the Excel
        // filter's own render path) -- keyed only by the grid's own id, not by which dialog owns it -- and
        // closing an Excel filter unconditionally destroys that whole wrapper as part of its own cleanup
        // (CheckBoxFilterBase.prototype.destroy, ej2-grids' checkbox-filter-base.js), taking down anything
        // else still living inside it.
        //
        // A prior fix round called this only from openExcelFilterThenDeferColumnChooser below, right before
        // that one specific click handler closes an open filter -- but CheckBoxFilterBase.prototype.btnClick
        // routes Cancel, Apply, *and* OK into that same destructive closeDialog(), and the dialog's own
        // `close` event (Escape, outside-click) is wired to the same closeDialog.bind(this) -- and this
        // file's own closeMenuIfSameIconClickedAgain (below) closes a filter via a different, unguarded
        // `.e-flat` click of its own. Patching each of those individually is exactly the reactive
        // whack-a-mole this bug's history has already shown doesn't converge: whichever close path fires,
        // if the Column Chooser happens to be sitting in the wrapper at that moment, it gets destroyed.
        //
        // Running this unconditionally at the filter's own *open* time instead makes the invariant "the
        // Column Chooser is never inside the shared wrapper while an Excel filter dialog exists" hold
        // regardless of how that filter is later closed -- by the time any close path runs, the rescue has
        // already happened. Harmless/no-op when the Column Chooser was never opened yet, or already lives
        // outside the wrapper (the `.closest()` check below is idempotent).
        const rescueColumnChooserFromSharedWrapper = () => {
            const columnChooserDlgElement = (gridRef.current as unknown as { columnChooserModule?: { dlgObj?: { element: HTMLElement } } })
                ?.columnChooserModule?.dlgObj?.element;
            if (columnChooserDlgElement?.closest(`#${REPORTS_GRID_POPUP_ID}`)) {
                gridElement.appendChild(columnChooserDlgElement);
            }
        };

        // resetDialogAppend (ej2-grids' util.js) inserts the shared wrapper as `document.body`'s own
        // firstChild -- a sibling of the app's <FluentProvider> root, not a descendant of it. Every Fluent 9
        // design token (`--spacingHorizontalM`, `--colorNeutralForeground2`, etc.) that reportsGridDialogStyles.tsx's
        // `var(...)` calls read is a CSS custom property FluentProvider sets on its own element, so it only
        // inherits down its own subtree -- a dialog reparented into this wrapper sits outside that subtree
        // entirely, and every one of those var() references resolves to nothing. That's invisible for rules
        // that only ever set a color or a shadow (the browser just keeps whatever was already painted), but
        // fatal for the entity filter tab strip: its layout depends on padding values built entirely out of
        // spacing tokens, so losing them collapses the whole strip to 0 padding -- the tabs render flush
        // against each other with no gap, indistinguishable from unstyled text. Copying the resolved custom
        // properties onto the wrapper itself re-establishes the inheritance chain regardless of where in the
        // DOM Syncfusion decided to park it.
        const inheritFluentDesignTokensOntoSharedWrapper = (popupWrapper: HTMLElement) => {
            const fluentProviderRoot = document.querySelector<HTMLElement>('.fui-FluentProvider');
            if (!fluentProviderRoot) {
                return;
            }
            const fluentProviderStyle = getComputedStyle(fluentProviderRoot);
            for (let i = 0; i < fluentProviderStyle.length; i++) {
                const property = fluentProviderStyle.item(i);
                if (property.startsWith('--')) {
                    popupWrapper.style.setProperty(property, fluentProviderStyle.getPropertyValue(property));
                }
            }
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) {
                        return;
                    }
                    const popupWrapper =
                        node.id === REPORTS_GRID_POPUP_ID ? node : node.querySelector<HTMLElement>(`#${REPORTS_GRID_POPUP_ID}`);
                    if (popupWrapper) {
                        inheritFluentDesignTokensOntoSharedWrapper(popupWrapper);
                    }
                    const dialog = node.classList.contains('e-excelfilter') ? node : node.querySelector<HTMLElement>('.e-excelfilter');
                    if (dialog) {
                        // Marked unconditionally, before the positioning below, so a filter icon still fills
                        // in even if repositioning itself fails for some unexpected reason -- the icon state
                        // and the dialog's own position are independent facts about the same dialog opening.
                        markFilterIconOpenState(dialog, true);
                        // General rescue (see rescueColumnChooserFromSharedWrapper above): runs every time an
                        // Excel filter dialog opens, by any means, so the Column Chooser is never left in the
                        // shared wrapper by the time this (or any other) filter dialog is later closed.
                        rescueColumnChooserFromSharedWrapper();
                        try {
                            const filterIcon = getFilterIconForDialog(dialog);
                            if (filterIcon) {
                                positionGridDialog(dialog, filterIcon);
                            }
                        } catch {
                            // Positioning is a visual nicety; leaving Syncfusion's own position in place is
                            // an acceptable fallback and must never take down the rest of this mutation
                            // batch (the entity-tab injection and row-tagging below).
                        }
                        restoreDialogFocusIfLostToReparent(dialog, '.e-searchinput');
                    }
                    const chooserDialog = node.classList.contains('e-ccdlg') ? node : node.querySelector<HTMLElement>('.e-ccdlg');
                    const chooserButton = document.querySelector<HTMLElement>(
                        `#${REPORTS_GRID_ID} .e-toolbar-item.e-cc-toolbar .e-tbar-btn`
                    );
                    if (chooserDialog && chooserButton) {
                        // Viewport clamp only in bulk edit: browse still has the toolbar Search box to the
                        // right of Columns, so Syncfusion's native right-aligned placement fits. Edit drops
                        // Search, making Columns rightmost -- CSS forces 280px while Syncfusion assumes 250,
                        // so the dialog clips the viewport edge unless we clamp. Excel filter clamping above
                        // is unchanged.
                        if (bulkEditSession.isActive) {
                            try {
                                positionGridDialog(chooserDialog, chooserButton);
                            } catch {
                                // Same rationale as the filter dialog above.
                            }
                        }
                        restoreDialogFocusIfLostToReparent(chooserDialog, '.e-ccsearch');
                    }
                    const dialogConfig = dialog ? entityGroupFilterColumnsByUid.get(dialog.getAttribute('uid') ?? '') : undefined;
                    if (dialog && dialogConfig) {
                        injectEntityFilterTabs(dialog);
                        openEntityGroupFilterConfig = dialogConfig;
                    }
                    if (openEntityGroupFilterConfig) {
                        if (node.classList.contains('e-ftrchk')) {
                            tagFilterRow(node, openEntityGroupFilterConfig);
                        } else {
                            node.querySelectorAll<HTMLElement>('.e-ftrchk').forEach((row) =>
                                tagFilterRow(row, openEntityGroupFilterConfig!)
                            );
                        }
                    }
                });
                mutation.removedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) {
                        return;
                    }
                    const dialog = node.classList.contains('e-excelfilter') ? node : node.querySelector<HTMLElement>('.e-excelfilter');
                    if (dialog) {
                        markFilterIconOpenState(dialog, false);
                        if (dialog.getAttribute('uid') === openEntityGroupFilterConfig?.columnUid) {
                            openEntityGroupFilterConfig = null;
                        }
                        // The Excel filter's own close/cleanup has now genuinely finished (its dialog element
                        // is actually out of the DOM, not just requested to close) -- including any shared
                        // wrapper teardown, per resetDialogAppend/CheckBoxFilterBase.prototype.destroy above.
                        // Safe to fire a deferred Column Chooser open now, if one is waiting.
                        openPendingColumnChooserIfAny();
                    }
                });
            });
        });

        // Observes `document.body` (not `gridElement`) for the same reason `getFilterIconForDialog` above
        // looks up fresh from `document` -- this must keep seeing new dialogs even after Syncfusion recreates
        // its own internal grid DOM out from under a `gridElement` reference captured once at effect setup.
        observer.observe(document.body, { childList: true, subtree: true });

        const openPendingColumnChooserIfAny = () => {
            const pending = pendingColumnChooserOpenRef.current;
            if (!pending) {
                return;
            }
            pendingColumnChooserOpenRef.current = null;
            if (pendingColumnChooserOpenFallbackTimeoutRef.current !== null) {
                clearTimeout(pendingColumnChooserOpenFallbackTimeoutRef.current);
                pendingColumnChooserOpenFallbackTimeoutRef.current = null;
            }
            // Looked up fresh via gridRef rather than closing over the `grid` local above -- by the time this
            // fires the grid may have remounted (e.g. a folder change while the dialogs were open).
            gridRef.current?.createColumnchooser(pending.x, pending.y, pending.target);
        };

        // Guards the collision described above: the Column Chooser toolbar button is clicked while an Excel
        // filter dialog is still open. Intercepted here, ahead of Syncfusion's own toolbar click handling
        // (ej2-grids' toolbar.js, the `_columnchooser` case, which would otherwise call createColumnchooser()
        // immediately), so the Excel filter can be closed first -- via the same "click its own Cancel button"
        // technique closeMenuIfSameIconClickedAgain below uses, which routes through Syncfusion's normal
        // close path rather than touching the DOM directly -- and the Column Chooser's own open deferred
        // until the MutationObserver above confirms that close has genuinely finished (see
        // openPendingColumnChooserIfAny/pendingColumnChooserOpenFallbackTimeoutRef).
        //
        // The reverse direction (opening the Excel filter while the Column Chooser is open) does not need
        // the same guard: the Column Chooser's normal close (ColumnChooser.prototype.hideDialog -> dlgObj.
        // hide()) only toggles a CSS class and never touches the shared wrapper -- only ColumnChooser.
        // prototype.destroy() removes it, and that only runs when the module itself is torn down (grid
        // unmount/refresh), not on a normal user-driven close.
        // rescueColumnChooserFromSharedWrapper (defined above, before the observer) now runs generally, every
        // time any Excel filter dialog opens -- which already covers this click handler's own collision,
        // since the filter it's about to close must already be open (that's the guard below), and its own
        // opening was necessarily an earlier, separate click/task. A MutationObserver callback is always
        // queued as a microtask, which drains before the browser dispatches the *next* click event -- so the
        // general rescue for that earlier open is guaranteed to have already run by the time this handler's
        // synchronous code executes.
        //
        // The call below is kept anyway, immediately before the close it guards, as a defensive, effectively
        // redundant confirmation rather than the primary mechanism -- cheap to keep because the function's
        // own `.closest()` check is idempotent (a no-op once the element is already outside the wrapper), and
        // it removes any reliance on the microtask-ordering argument above holding under some future
        // Syncfusion change (e.g. if a later version made the filter's own open path asynchronous). Given
        // this bug's history of reactive fixes that didn't converge, the cost of one extra guaranteed-safe
        // call here is far lower than the cost of another live-only timing gap.
        const columnChooserToolbarButtonSelector = `#${REPORTS_GRID_ID} .e-toolbar-item.e-cc-toolbar .e-tbar-btn`;
        const openExcelFilterThenDeferColumnChooser = (event: MouseEvent) => {
            const chooserButton = (event.target as HTMLElement)?.closest<HTMLElement>(columnChooserToolbarButtonSelector);
            if (!chooserButton) {
                return;
            }
            const openFilterDialog = getReportsGridDialogElement<HTMLElement>('.e-excelfilter');
            if (!openFilterDialog) {
                return;
            }

            // Mirrors the x/y Syncfusion's own toolbar click handler computes for this exact case (ej2-grids'
            // toolbar.js, the `_columnchooser` case): `.e-ccdiv` is the toolbar item wrapper div (the same
            // element as `.e-toolbar-item.e-cc-toolbar` above -- column-chooser.js renders it with both
            // classes), and the target passed through is the raw click target, exactly like
            // `toolbarargs.originalEvent.target`. Found via `.closest()` from the button that was actually
            // clicked (not a fresh grid-rooted query) so this can't go stale if Syncfusion has recreated its
            // own internal toolbar DOM since this effect last ran.
            const chooserToolbarItem = chooserButton.closest<HTMLElement>('.e-ccdiv');
            if (!chooserToolbarItem) {
                return;
            }
            const chooserBounds = chooserToolbarItem.getBoundingClientRect();
            pendingColumnChooserOpenRef.current = {
                x: chooserBounds.left,
                y: chooserBounds.top + chooserToolbarItem.offsetTop,
                target: event.target as HTMLElement,
            };
            if (pendingColumnChooserOpenFallbackTimeoutRef.current !== null) {
                clearTimeout(pendingColumnChooserOpenFallbackTimeoutRef.current);
            }
            pendingColumnChooserOpenFallbackTimeoutRef.current = setTimeout(openPendingColumnChooserIfAny, 250);

            // Stops Syncfusion's own toolbar click handling from also acting on this same click -- without
            // this it would call createColumnchooser() immediately, racing the Excel filter close below.
            event.stopPropagation();
            event.preventDefault();
            // Must run before the close below -- see rescueColumnChooserFromSharedWrapper's own comment for
            // why this has to happen unconditionally, ahead of the destructive step, rather than after it.
            rescueColumnChooserFromSharedWrapper();
            openFilterDialog.querySelector<HTMLElement>('.e-flat')?.click();
        };
        // Capture phase, on `document`, for the same reasons as closeMenuIfSameIconClickedAgain below: it
        // has to run ahead of Syncfusion's own bubble-phase toolbar click handling, and must keep working
        // after Syncfusion recreates its own internal grid DOM.
        document.addEventListener('click', openExcelFilterThenDeferColumnChooser, true);

        // Syncfusion's own filter icon click handler no-ops when the clicked icon's column already has its
        // menu open (it only handles switching to a *different* column, or a document-level click outside
        // closing it -- clicking the same icon again is explicitly excluded from that path, and would
        // otherwise just sit there open). This adds the missing toggle. It has to run in the capture phase,
        // ahead of Syncfusion's own bubble-phase click handling: by the time a bubble-phase listener sees
        // this click, Syncfusion has *already* synchronously (re)opened the dialog for it, so a dialog found
        // in the DOM at that point doesn't distinguish "was already open" from "just opened by this same
        // click". In the capture phase that hasn't happened yet, so finding the dialog here means it was
        // truly already open. Closing it via its own Cancel button routes through Syncfusion's normal
        // close/cleanup (rather than removing the DOM node directly) so its internal "is this column's menu
        // open" state stays in sync -- and stopping propagation keeps Syncfusion's own handler from then
        // reopening it when the click continues on to the bubble phase.
        //
        // This close routes through the same wrapper-destroying CheckBoxFilterBase.prototype.destroy as
        // every other Excel filter close, but does not need its own rescueColumnChooserFromSharedWrapper()
        // call: `openDialog` here is necessarily a dialog that was already open before this click (that's
        // the whole point of the toggle), so its opening already triggered the general, open-time rescue in
        // the observer above -- there is nothing left to rescue at close time that wasn't already handled.
        const filterIconMappingPrefix = 'e-flmenu-';
        const closeMenuIfSameIconClickedAgain = (event: MouseEvent) => {
            const filterIcon = (event.target as HTMLElement)?.closest<HTMLElement>('.e-filtermenudiv');
            const columnUid = filterIcon?.getAttribute('e-mappinguid')?.replace(filterIconMappingPrefix, '');
            const openDialog = columnUid ? document.body.querySelector<HTMLElement>(`.e-excelfilter[uid="${columnUid}"]`) : null;
            if (!openDialog) {
                return;
            }
            event.stopPropagation();
            openDialog.querySelector<HTMLElement>('.e-flat')?.click();
        };
        // Listens on `document` (not `gridElement`) for the same staleness reason as the observer above --
        // a click on the current, live filter icon must still reach this handler even if it lands on a grid
        // DOM node Syncfusion swapped in after this effect last ran.
        document.addEventListener('click', closeMenuIfSameIconClickedAgain, true);
        document.addEventListener('click', resyncSelectAllWithActiveTab);
        document.addEventListener('keyup', navigateEntityFilterVisibleRows, true);

        // Syncfusion's Column Chooser dialog (rendered non-modal, per ColumnChooser.prototype.renderDlgContent
        // -- it never sets `isModal: true` on the underlying Dialog) has no focus trap of its own: Tab from its
        // last control (Cancel) or Shift+Tab from its first (the search box, or the first checklist row when
        // search is hidden) walks straight out into whatever the grid's surrounding page happens to render
        // next in document order, while the dialog itself stays open and visually in place. A keyboard user is
        // left with an open popup they can no longer reach by Tabbing, with no indication where focus went.
        // It also has no ArrowUp/Down navigation (unlike ExcelFilterBase / CheckBoxFilterBase): arrows stay
        // stuck in the search box, and on a checkbox the browser scrolls `.e-cc-contentdiv` instead of moving
        // focus. Escape still closes it correctly (ColumnChooser.prototype.keyUpHandler's own `keyup`
        // listener, wired directly on the dialog element, is untouched by this).
        //
        // Delegated on `document` in the capture phase, ahead of the browser's own default key handling, for
        // the same staleness reason as every other listener in this effect: the dialog can be reparented (see
        // rescueColumnChooserFromSharedWrapper) or have its checklist rebuilt from scratch on every keystroke
        // in the search box (ColumnChooser.prototype.columnChooserSearch), so both the dialog lookup and the
        // focusable-element list are recomputed fresh on every key press rather than cached.
        const handleColumnChooserKeyboard = (event: KeyboardEvent) => {
            const isTab = event.key === 'Tab';
            const isArrow = (event.key === 'ArrowUp' || event.key === 'ArrowDown') && !event.altKey;
            if (!isTab && !isArrow) {
                return;
            }
            const dialog = getReportsGridDialogElement<HTMLElement>('.e-ccdlg.e-popup-open');
            if (!dialog || !(event.target instanceof HTMLElement) || !dialog.contains(event.target)) {
                return;
            }
            const focusableElements = Array.from(
                dialog.querySelectorAll<HTMLElement>('.e-ccsearch, .e-cc-chbox, .e-cc_okbtn, .e-cc-cnbtn')
            );
            if (focusableElements.length === 0) {
                return;
            }
            if (isArrow) {
                event.preventDefault();
                const currentIndex = focusableElements.indexOf(event.target);
                const nextIndex =
                    event.key === 'ArrowUp'
                        ? currentIndex <= 0
                            ? focusableElements.length - 1
                            : currentIndex - 1
                        : (currentIndex + 1) % focusableElements.length;
                focusableElements[nextIndex]?.focus();
                return;
            }
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (!firstElement || !lastElement) {
                return;
            }
            if (event.shiftKey && event.target === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && event.target === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };
        document.addEventListener('keydown', handleColumnChooserKeyboard, true);

        // Browser zoom fires resize, which is the case this exists for: an open dialog otherwise keeps the
        // coordinates it was given at the previous viewport size and strands itself away from its own
        // control. Repositioning rather than closing -- closing on zoom punishes exactly the users this is
        // meant to help. Coalesced through rAF because resize fires continuously during a drag.
        let repositionFrameId: number | null = null;
        const repositionOpenDialogs = () => {
            if (repositionFrameId !== null) {
                return;
            }
            repositionFrameId = requestAnimationFrame(() => {
                repositionFrameId = null;

                const openFilterDialog = getReportsGridDialogElement<HTMLElement>('.e-excelfilter');
                const filterIcon = openFilterDialog ? getFilterIconForDialog(openFilterDialog) : null;
                if (openFilterDialog && filterIcon) {
                    positionGridDialog(openFilterDialog, filterIcon);
                }

                // Same edit-only gate as the MutationObserver branch above -- browse keeps Syncfusion's
                // native placement; edit reclamps so a zoom/resize cannot reintroduce the right-edge clip.
                if (bulkEditSession.isActive) {
                    const openChooserDialog = getReportsGridDialogElement<HTMLElement>('.e-ccdlg.e-popup-open');
                    const chooserButton = document.querySelector<HTMLElement>(
                        `#${REPORTS_GRID_ID} .e-toolbar-item.e-cc-toolbar .e-tbar-btn`
                    );
                    if (openChooserDialog && chooserButton) {
                        positionGridDialog(openChooserDialog, chooserButton);
                    }
                }
            });
        };
        window.addEventListener('resize', repositionOpenDialogs);

        return () => {
            observer.disconnect();
            document.removeEventListener('click', openExcelFilterThenDeferColumnChooser, true);
            document.removeEventListener('click', closeMenuIfSameIconClickedAgain, true);
            document.removeEventListener('click', resyncSelectAllWithActiveTab);
            document.removeEventListener('keyup', navigateEntityFilterVisibleRows, true);
            document.removeEventListener('keydown', handleColumnChooserKeyboard, true);
            window.removeEventListener('resize', repositionOpenDialogs);
            if (repositionFrameId !== null) {
                cancelAnimationFrame(repositionFrameId);
            }
            if (pendingColumnChooserOpenFallbackTimeoutRef.current !== null) {
                clearTimeout(pendingColumnChooserOpenFallbackTimeoutRef.current);
                pendingColumnChooserOpenFallbackTimeoutRef.current = null;
            }
            pendingColumnChooserOpenRef.current = null;
        };
    }, [t, selectedFolderFilter, filteredReportsResponse, isLoading, hasLatestCalculationStatus, bulkEditSession.isActive]);

    const displayCalculationStatusInTable = (taxReturnItemKey: string, calculationExecutionStatus: CalculationExecutionStatus) => {
        if (calculationExecutionStatus.statusCode === CalculationExecutionStatusCode.Executing) {
            updateReturnLastExecutedDateInList(parseInt(taxReturnItemKey), t('calculating'), undefined);
        } else if (calculationExecutionStatus.statusCode === CalculationExecutionStatusCode.Canceled) {
            updateReturnLastExecutedDateInList(parseInt(taxReturnItemKey), t('canceled'), undefined);
        }
    };

    useEffect(() => {
        // For '/' root page, track the utm event if present.
        const { isValid, params } = getUtmParams();
        if (params && isValid && isReady) {
            trackEvent(REDIRECTED_EVENT_NAME, { action_name: REDIRECTED_ACTION_NAME, ...params });
            cleanUtmParamsFromUrl();
        }
    }, [getUtmParams, cleanUtmParamsFromUrl, trackEvent, isReady]);

    useEffect(() => {
        registerGlobalDependency('ReportsTableTrigger', (calculationStatus: IExecutionStatusStorage) => {
            if (calculationStatus.taxReturnItemKey && calculationStatus.statusCode !== CalculationExecutionStatusCode.Executing) {
                getLatestExecutionInformation({ reportId: calculationStatus.taxReturnItemKey }).then((res) => {
                    updateReturnLastExecutedDateInList(
                        calculationStatus.taxReturnItemKey || 0,
                        res?.executionDate!,
                        res?.userFullName!,
                        false
                    );
                });
            } else {
                displayCalculationStatusInTable(calculationStatus.taxReturnItemKey?.toString() || '', calculationStatus);
            }
        });

        fetchAllCalculationExecutionStatuses().then((statusMap: { [key: string]: CalculationExecutionStatus } | undefined) => {
            if (statusMap) {
                for (const [key, value] of Object.entries(statusMap)) {
                    displayCalculationStatusInTable(key, value);
                }
            }
            setHasLatestCalculationStatusTrue();
        });
    }, [filteredReportsResponse]);

    useEffect(() => {
        if (recentlyCreatedTaxReturnItemKeys) {
            getTaxReturns({ data: { priorityTaxReturnItemKeys: recentlyCreatedTaxReturnItemKeys } });
            setRecentlyCreatedTaxReturnItemKeys(undefined);
        }
    }, [recentlyCreatedTaxReturnItemKeys]);

    useEffect(() => {
        // Intentionally omits `recentlyCreatedTaxReturnItemKeys` from deps: when the effect above just fired a
        // priority fetch and reset that state to undefined, this effect must NOT also re-run and issue a second,
        // non-priority fetch that could overwrite the priority-sorted result. Reading it here (without depending
        // on it) lets this effect skip its own fetch on the render where a priority fetch just happened instead.
        if (!recentlyCreatedTaxReturnItemKeys) {
            getTaxReturns({ data: memoizedFilters });
        }
    }, [memoizedFilters]);

    const { storeSearchValueToHighlight, highlightSearchValueOnRender, clearSearch, hasMatch } =
        useHighlightSearchedValues(getReportPropertyValueByFieldName);
    const { clearSearch: clearSearchBar } = useSearchTable();

    const tableKey = `ReportsTable-${selectedFolderFilter?.key ?? ''}`;

    // The grid remounts fresh (key={tableKey} below) whenever the folder filter changes, which silently drops
    // its own filterSettings -- this state has to be reset in step, or the "Clear filters" toolbar item keeps
    // showing (and doing nothing) for a grid that no longer has any column filters applied.
    // selectedRowIdsRef lives on this component (not the grid), so it must be cleared too: otherwise Bulk edit
    // stays enabled from folder-A ids after the remounted folder-B grid has no visible selection.
    useEffect(() => {
        setHasActiveColumnFilters(false);
        selectedRowIdsRef.current.clear();
        // Select-all is row-set state too: left set, restoreSelectAllIfNeeded re-selects every row of the new folder.
        selectAllActiveRef.current = false;
        setDisableActionButtons(true);
        // The search is row-set state as well; drop the highlight and the search bar text with the old grid.
        clearSearch();
        clearSearchBar(gridRef);
    }, [selectedFolderFilter]);

    const updateFolders = (items: ReturnFolderDto[]) => {
        const folders: IFilterItem[] = [];
        items.forEach((item) => {
            if (!folders.some((e) => e.key === item.folderKey?.toString())) {
                folders.push({
                    key: item.folderKey?.toString() ?? '',
                    label: item.folderName ? `${item.folderName} (${item.returnCount})` : '',
                    searchBy: [item.folderName ?? ''],
                });
            }
        });

        setFolders(folders);
    };

    useEffect(() => {
        if (isResponseArrayValid(folderItems)) {
            updateFolders(folderItems ?? []);
        }
    }, [folderItems]);

    const handleBrowseNameEdit = (item: Report, index: number | string | undefined) => {
        const taxReturn = adaptReportToShowableTaxReturnItemRecord(item);
        setSelectedCellIndex({
            cellIndex: 2,
            rowIndex: typeof index === 'number' ? index : parseInt(String(index), 10),
        });
        setSelectedReturn(taxReturn);
        openEditReturnModal();
    };

    const handleBrowseNameDelete = (item: Report, index: number | string | undefined) => {
        setReturnToDelete(item.id);
        setSelectedCellIndex({
            cellIndex: 2,
            rowIndex: parseInt(String(index), 10),
        });
        openDeleteReturnModal();
    };

    const reportNameTemplateTruncated = (item: Report) => {
        const index = (item as { index?: number | string }).index;
        return (
            <BrowseReportNameCell
                item={item}
                index={index}
                isCreateNewReturnEnabled={isCreateNewReturnEnabled}
                isEditCalculationEnhancementsEnabled={isEditCalculationEnhancementsEnabled}
                viewTaxReturnsEnabled={functionalAccess?.viewTaxReturnsEnabled}
                editReportsButtonEnabled={functionalAccess?.editReportsButtonEnabled}
                deleteReportsButtonEnabled={functionalAccess?.deleteReportsButtonEnabled}
                onEdit={handleBrowseNameEdit}
                onDelete={handleBrowseNameDelete}
            />
        );
    };

    const reportHeaderTemplate = (item: ColumnModel) => {
        return (
            <DefaultTooltipHostV9 content={item.headerText ?? ''} relationship='label'>
                <label>{item.headerText}</label>
            </DefaultTooltipHostV9>
        );
    };

    const isCellTextDisabled = () => !functionalAccess?.viewTaxReturnsEnabled;

    const renderTextCell = (displayValue: string) => {
        const isDisabledText = isCellTextDisabled();
        return (
            <DefaultTooltipHostV9
                content={displayValue}
                relationship='label'
                containerClassName={isDisabledText ? styles.disabledItem : undefined}
            >
                <label tabIndex={-1} className={isDisabledText ? styles.disabledItem : undefined}>
                    {displayValue}
                </label>
            </DefaultTooltipHostV9>
        );
    };

    const reportStatusTemplate = (item: Report) => {
        return (
            <DefaultTooltipHostV9 content={t(item.status ?? '').toString()} relationship='label'>
                <ReportStatusButton
                    key={item.id}
                    status={item.status ?? ReportStatus.NotStarted}
                    onChange={(status) => {
                        handleStatusChange(item.id!, status);
                    }}
                    setSelectedCellIndex={setSelectedCellIndex}
                    isCell={true}
                    tabIndex={-1}
                    disabled={!functionalAccess?.viewTaxReturnsEnabled}
                />
            </DefaultTooltipHostV9>
        );
    };

    // Bulk edit has no per-row status dropdown -- only Set-for-all (or future inline edits).
    const bulkEditStatusTemplate = (item: Report) => renderTextCell(t(camelCase(item.status ?? '')).toString());

    const statusFilterItemTemplate = (data: { status?: ReportStatus }) => {
        return <>{t(camelCase(data.status ?? ''))}</>;
    };

    const getColumnTemplate = (fieldName: string) => {
        return (item: ReportTableRow) => {
            const propertyKey = fieldName as keyof ReportTableRow;
            return renderTextCell(item[propertyKey]?.toString() ?? '');
        };
    };

    const getBooleanColumnTemplate = (fieldName: string) => {
        return (item: ReportTableRow) => {
            const propertyKey = fieldName as keyof ReportTableRow;
            const displayValue = (item[propertyKey] ? t('booleanYes') : t('booleanNo')).toString();
            const isDisabledText = isCellTextDisabled();

            return (
                <DefaultTooltipHostV9
                    content={displayValue}
                    relationship='label'
                    containerClassName={isDisabledText ? styles.disabledItem : undefined}
                >
                    <label tabIndex={-1} className={isDisabledText ? styles.disabledItem : undefined}>
                        {displayValue}
                    </label>
                </DefaultTooltipHostV9>
            );
        };
    };

    const handleStatusChange = async (reportId: number, key: ReportStatus) => {
        try {
            //todo: processing indicator
            await mutateAsync({ reportId: reportId, params: { reportStatus: key } });
            //todo: error indicator
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };

    const handleReportToExport = (): ISelectedTaxReturnsAndFilters => {
        // Resolve from selectedRowIdsRef (not grid.getSelectedRecords(), which truncates under virtualization),
        // limited to rows still visible so folder/cart/export match the count and label the toolbar shows.
        const selectedRecords = getSelectedRows();
        return getSelectedTaxReturnsAndFilters(gridRef, getTaxReturnsRequest, selectedRecords);
    };

    if (isError) {
        return (
            <div>
                <label>{t('error')}</label>
            </div>
        );
    }

    const selectionSettings: SelectionSettingsModel = {
        checkboxOnly: true,
        persistSelection: true,
    };

    const queryCellInfo = (args: QueryCellInfoEventArgs) => {
        if (args.cell?.classList.contains('e-gridchkbox')) {
            args.cell?.querySelector('input')?.setAttribute('tabindex', '-1');
        }

        highlightSearchValueOnRender(args);
    };

    const handleActionComplete = (args: GridActionEventArgs) => {
        if (args.requestType === 'searching') {
            scrollToTop(gridRef.current);
        }
        // Syncfusion's Column Chooser fires this requestType (untyped in its Action union) when the user applies
        // a visibility change, distinct from resize/reorder actions -- exactly the "column list changed" signal
        // that should reveal the Reset Grid toolbar item.
        if ((args.requestType as string) === 'columnVisibilityUpdate') {
            const grid = gridRef.current;
            if (grid) {
                setHasHiddenResettableColumns(hasHiddenResettableColumn(grid));
                setHiddenColumnFields(getHiddenResettableColumnFields(grid));
            }
        }
        // Fires for both applying and clearing a per-column filter, so it doubles as the signal to show/hide
        // the Clear Filters toolbar item based on whether any column filter is currently active.
        if (args.requestType === 'filtering') {
            setHasActiveColumnFilters((gridRef.current?.filterSettings.columns?.length ?? 0) > 0);
        }
    };

    const handleActionBegin = (args: GridActionEventArgs) => {
        if (args.requestType === 'sorting') {
            const direction = (args as SortEventArgs).direction;
            // A refresh with no direction must not wipe the direction the restored browse sort uses.
            if (direction) {
                sortingDirection.current = direction;
            }
        }

        storeSearchValueToHighlight(args);
    };

    const searchSetting: SearchSettingsModel = {
        fields: [
            name,
            entityCodeFilterName,
            entityFilterName,
            'description',
            jurisdictionFilterName,
            caseFilterName,
            userFullName,
            'customGroupName',
            'customRangeName',
            'postAmountOption',
            'postAmountGroupName',
        ],
    };

    const filterSettings: FilterSettingsModel = { type: 'Excel' };

    const isColumnVisible = (field: string): boolean => !hiddenColumnFields.includes(field);

    const getHiddenResettableColumnFields = (grid: GridComponent): string[] =>
        (grid.columns as ColumnModel[])
            .filter((column) => column.field && RESETTABLE_COLUMN_FIELDS.includes(column.field) && !column.visible)
            .map((column) => column.field as string);

    const hasHiddenResettableColumn = (grid: GridComponent): boolean =>
        !areColumnFieldSetsEqual(getHiddenResettableColumnFields(grid), DEFAULT_HIDDEN_COLUMN_FIELDS);

    const resetColumnVisibility = () => {
        const grid = gridRef.current;
        if (!grid) {
            return;
        }
        // Assigning column.width below is indistinguishable, internally, from the user manually drag-resizing
        // every column -- Syncfusion's width service treats it the same way and permanently switches the table
        // from filling its container (width: 100%, the state a freshly loaded page is in) to a fixed pixel sum
        // of the column widths, which leaves a gap on screens wider than that sum. Forcing it back to 100%
        // after refresh is what actually makes this match a freshly loaded page, not just the individual
        // column widths.
        //
        // widthService.setWidthToTable is an undocumented Syncfusion internal. With frozen columns,
        // show/hide/refreshColumns also invoke it via freeze refresh (often outside our call stack), so a
        // missing or throwing implementation would otherwise abort the reset and leave Reset Grid stuck
        // visible. Wrap only for this reset (restore in finally) so we don't nest wrappers on repeat clicks
        // or swallow genuine width failures for the rest of the session.
        type WidthServiceWithSetWidth = { setWidthToTable?: (reset?: boolean) => void };
        const widthService = grid.widthService as WidthServiceWithSetWidth | undefined;
        const priorSetWidthToTable = widthService?.setWidthToTable;
        if (widthService) {
            widthService.setWidthToTable = (reset?: boolean) => {
                try {
                    priorSetWidthToTable?.call(widthService, reset);
                } catch {
                    // Swallow -- public reset + React/storage updates below must still run.
                }
            };
        }

        try {
            // Directly mutating column.visible + refreshColumns() only updates the column model -- it leaves
            // whatever DOM/CSS state Syncfusion uses to actually hide a column (e.g. its 'e-hide' class) stale,
            // so a column stayed visually hidden until some unrelated action (like a manual resize) forced a
            // fuller refresh. showColumns()/hideColumns() are Syncfusion's own public API for this and route
            // through the same internal show/hide pipeline the Column Chooser itself uses (including firing the
            // 'columnstate' actionComplete that triggers autoFitColumns(), so column widths get corrected too).
            grid.showColumns(RESETTABLE_COLUMN_FIELDS, 'field');
            grid.hideColumns(DEFAULT_HIDDEN_COLUMN_FIELDS, 'field');
            // Reset Grid previously only undid visibility changes -- a column dragged wider/narrower stayed that
            // way. Restoring each column's declared default width (and refreshColumns() to repaint it) makes this
            // a true reset back to the same layout a freshly loaded page would show.
            RESETTABLE_COLUMN_FIELDS.forEach((field) => {
                const column = grid.getColumnByField(field);
                if (column) {
                    column.width = DEFAULT_COLUMN_WIDTHS[field];
                }
            });
            grid.refreshColumns();
            widthService?.setWidthToTable?.(true);
            setHasHiddenResettableColumns(false);
            setHiddenColumnFields(DEFAULT_HIDDEN_COLUMN_FIELDS);
        } finally {
            if (widthService) {
                widthService.setWidthToTable = priorSetWidthToTable;
            }
        }
    };

    // Syncfusion hardcodes its Column Chooser search box to the grid's generic 'Search' locale string
    // (shared with the toolbar's own Search item), so the placeholder text has to be set on the DOM
    // directly rather than through localization to avoid also renaming the toolbar Search box.
    const handleBeforeOpenColumnChooser = () => {
        const searchInput = getReportsGridDialogElement<HTMLInputElement>('.e-ccdlg .e-ccsearch');
        if (searchInput) {
            searchInput.placeholder = t('searchColumns').toString();
        }

        // Syncfusion reuses the same Column Chooser dialog instance across opens rather than recreating it,
        // and restores the list's previous scroll position as part of its own (synchronous) show logic that
        // runs right after this event -- so the column list otherwise stays scrolled wherever it was left
        // last time instead of opening back at the top. Setting scrollTop here directly gets overwritten by
        // that later restoration within the same call stack; deferring to a macrotask runs this after it.
        // The same deferral is when we correct position in bulk edit: Syncfusion assumes a 250px dialog
        // width, then CSS paints ~280px — positionGridDialog uses the laid-out width + viewport clamp so
        // Columns (rightmost once Search is omitted) does not clip off the right edge. Browse keeps
        // Syncfusion's native placement; Search sits to the right of Columns there, so no clamp is needed.
        setTimeout(() => {
            const contentDiv = getReportsGridDialogElement('.e-ccdlg .e-cc-contentdiv');
            if (contentDiv) {
                contentDiv.scrollTop = 0;
            }

            if (!bulkEditSession.isActive) {
                return;
            }

            const chooserDialog = getReportsGridDialogElement<HTMLElement>('.e-ccdlg');
            const chooserButton = document.querySelector<HTMLElement>(`#${REPORTS_GRID_ID} .e-toolbar-item.e-cc-toolbar .e-tbar-btn`);
            if (chooserDialog && chooserButton) {
                positionGridDialog(chooserDialog, chooserButton);
            }
        }, 0);
    };

    // Fade unselected browse rows, then commitEnter unmounts the host. isEntering locks the pane
    // with pointer-events: none for that window so select-all / toolbar cannot be used.
    const enterBulkEditView = () => {
        if (bulkEditSession.isEntering || bulkEditSession.isActive) {
            return;
        }
        const selectedRows = getSelectedRows();
        const grid = gridRef.current;
        const currentRows = grid?.getRows() as HTMLElement[] | undefined;
        const selectedIdsInScope = new Set(selectedRows.map((row) => row.id));
        // Resolve each <tr> to its record by data-uid: under virtualization / frozen columns getRows() and
        // getCurrentViewRecords() are not guaranteed to line up by index.
        const rowsLeaving = currentRows?.filter((row) => {
            const uid = row.getAttribute('data-uid');
            const record = uid ? (grid?.getRowObjectFromUID(uid)?.data as ReportTableRow | undefined) : undefined;
            return record?.id != null && !selectedIdsInScope.has(record.id);
        });

        const plan = bulkEditSession.prepareEnter({
            selectedCount: selectedRows.length,
            listedCount: getSelectAllRowIds().length,
            leavingCount: rowsLeaving?.length ?? 0,
            viewSnapshot: {
                searchKey: grid?.searchSettings?.key ?? '',
                // Spread drops ChildProperty getters, `field` included -- restore needs it to re-resolve the
                // predicate's column on the remounted grid, so copy it explicitly.
                filterColumns: (grid?.filterSettings?.columns ?? []).map((column) => ({ ...column, field: column.field })),
                // Spread drops ChildProperty getters (field, direction) and would restore an empty sort.
                sortColumns: cloneSortColumns(grid?.sortSettings?.columns),
            },
        });

        if (enterBulkEditRafRef.current != null) {
            window.cancelAnimationFrame(enterBulkEditRafRef.current);
            enterBulkEditRafRef.current = null;
        }

        if (plan.shouldAnimateLeave && rowsLeaving) {
            rowsLeaving.forEach((row) => addClassNames(row, styles.rowLeaveTransition));
            enterBulkEditRafRef.current = requestAnimationFrame(() => {
                enterBulkEditRafRef.current = null;
                rowsLeaving.forEach((row) => addClassNames(row, styles.rowHidden));
            });
        }
        bulkEditSession.scheduleCommitEnter(selectedRows, ROW_LEAVE_DURATION_MS);
    };

    const exitBulkEditView = () => {
        bulkEditSession.beginExit(getSelectedRowIdsInScope());
    };

    const onToolbarClick = (args: ClickEventArgs) => {
        switch (args.item?.id) {
            case ADD_NEW_RETURN_TOOLBAR_ITEM_ID:
                openAddReturnPanel();
                break;
            case CLEAR_FILTERS_TOOLBAR_ITEM_ID:
                gridRef.current?.clearFiltering();
                setHasActiveColumnFilters(false);
                break;
            case RESET_GRID_TOOLBAR_ITEM_ID:
                resetColumnVisibility();
                break;
            case BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID:
                enterBulkEditView();
                break;
            case BULK_EDIT_SAVE_TOOLBAR_ITEM_ID:
            case BULK_EDIT_CANCEL_TOOLBAR_ITEM_ID:
                exitBulkEditView();
                break;
            default:
                break;
        }
    };

    const sortComparer = (reference: ValueType, comparer: ValueType): number => {
        return getSortComparer(reference, comparer, sortingDirection.current?.toString() || '');
    };

    type ReportsSelectionModule = {
        persistSelectedData?: ReportTableRow[];
        selectedRowState?: Record<string, boolean>;
        isHdrSelectAllClicked?: boolean;
        isPartialSelection?: boolean;
    };

    const getReportsSelectionModule = (): ReportsSelectionModule | undefined =>
        (gridRef.current as { selectionModule?: ReportsSelectionModule } | null)?.selectionModule;

    const isReportSelectable = (report: ReportTableRow) => report.id != null && !!functionalAccess?.viewTaxReturnsEnabled;

    const getSelectableReports = (): ReportTableRow[] => {
        const grid = gridRef.current;
        if (!grid) {
            return reportRows.filter(isReportSelectable);
        }
        const hasColumnFilter = (grid.filterSettings?.columns?.length ?? 0) > 0;
        const hasToolbarSearch = !!grid.searchSettings?.key?.trim();
        const filteredRecords = hasColumnFilter || hasToolbarSearch ? grid.getFilteredRecords?.() : undefined;
        const records = Array.isArray(filteredRecords) ? (filteredRecords as ReportTableRow[]) : reportRows;
        return records.filter(isReportSelectable);
    };

    const getIdsFromRowEventData = (data: object | object[] | undefined): number[] =>
        (Array.isArray(data) ? data : data ? [data] : [])
            .map((record) => (record as ReportTableRow)?.id)
            .filter((id): id is number => id != null);

    // selectedRowIdsRef keeps ids that a later filter/search hid (select-all, then filter). Only ids still in the
    // filtered scope may count as selected, otherwise bulk edit would pick up rows the user cannot see.
    const getSelectedRowIdsInScope = (): Set<number> => {
        const scopeIds = new Set(getSelectAllRowIds());
        return new Set([...selectedRowIdsRef.current].filter((id) => scopeIds.has(id)));
    };

    const getSelectedRows = (): ReportTableRow[] => {
        const idsInScope = getSelectedRowIdsInScope();
        return reportRows.filter((row) => row.id != null && idsInScope.has(row.id));
    };

    const syncSelectedRowIds = (records: ReportTableRow[]) => {
        selectedRowIdsRef.current = new Set(records.map((report) => report.id).filter((id): id is number => id != null));
    };

    // Select-all must cover the full filtered (or unfiltered) data set, not Syncfusion's
    // getSelectedRecords() / event payloads — under virtualization those only include materialized rows.
    const getSelectAllRowIds = (): number[] => {
        const grid = gridRef.current;
        if (!grid) {
            return [];
        }
        const hasColumnFilter = (grid.filterSettings?.columns?.length ?? 0) > 0;
        const hasToolbarSearch = !!grid.searchSettings?.key?.trim();
        const filteredRecords = hasColumnFilter || hasToolbarSearch ? grid.getFilteredRecords?.() : undefined;
        const records = Array.isArray(filteredRecords) ? (filteredRecords as ReportTableRow[]) : reportRows;
        return records.map((record) => record.id).filter((id): id is number => id != null);
    };

    const isHeaderCheckboxUnchecked = (): boolean => {
        const checkAllBox = gridRef.current?.element?.querySelector('.e-checkselectall');
        return Boolean(checkAllBox?.nextElementSibling?.classList.contains('e-uncheck'));
    };

    const notifySelectionChanged = () => {
        const selectedRecords = getSelectedRows();
        handleDisableActionButtons(selectedRecords, setDisableActionButtons);
        announce(t('selected_rows', { count: selectedRecords.length }).toString(), { batchId: 'reports-selected-rows' });
        scheduleBulkEditToolbarItemStateUpdate();
    };

    // Uses getSelectedRows() (ref ∩ current reportRows), not raw selectedRowIdsRef.size, so enable/label
    // stay aligned with export/cart and cannot enable Bulk edit on ids that are no longer in scope.
    // Runs a frame later via rAF; getSelectedRows reads the ref + latest reportRows from this closure.
    const flushBulkEditToolbarItemState = () => {
        const toolbarModule = getSyncfusionToolbarModule(gridRef.current);
        if (!toolbarModule) {
            return;
        }
        const selectedCount = getSelectedRows().length;
        toolbarModule.enableItems([BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID], selectedCount > 0);

        const label = (selectedCount > 1 ? t('bulkEdit') : t('edit')).toString();
        const button = gridRef.current?.element?.querySelector<HTMLElement>(`#${BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID}`);
        if (button) {
            const textEl = button.querySelector('.e-tbar-btn-text');
            if (textEl) {
                textEl.textContent = label;
            }
            button.setAttribute('title', label);
            button.setAttribute('aria-label', label);
        }
    };

    const scheduleBulkEditToolbarItemStateUpdate = () => {
        scheduleToolbarFlushRaf(bulkEditToolbarFlushRafRef, flushBulkEditToolbarItemState);
    };

    // Virtualization + persistSelection + even one isSelectable=false row (no functional access) turns on
    // Syncfusion isPartialSelection. After sort, setCheckAllState then compares persist count to the
    // 50-row viewport and forces e-stop; clicks always select and never uncheck.
    const syncHeaderCheckboxToSelection = () => {
        const grid = gridRef.current;
        if (!grid?.element) {
            return;
        }
        const selectableIds = getSelectableReports().map((report) => report.id as number);
        const selectedInScope = selectableIds.filter((id) => selectedRowIdsRef.current.has(id)).length;
        const nextState = selectAllActiveRef.current
            ? 'check'
            : selectableIds.length > 0 && selectedInScope === selectableIds.length
            ? 'check'
            : selectedInScope === 0
            ? 'uncheck'
            : 'stop';
        grid.element.querySelectorAll<HTMLInputElement>('.e-checkselectall').forEach((input) => {
            const frame = input.nextElementSibling;
            if (!(frame instanceof HTMLElement)) {
                return;
            }
            frame.classList.remove('e-check', 'e-stop', 'e-uncheck');
            if (nextState === 'check') {
                frame.classList.add('e-check');
                input.checked = true;
                input.indeterminate = false;
            } else if (nextState === 'uncheck') {
                frame.classList.add('e-uncheck');
                input.checked = false;
                input.indeterminate = false;
            } else {
                frame.classList.add('e-stop');
                input.checked = false;
                input.indeterminate = true;
            }
        });
        (grid as { checkAllRows?: string }).checkAllRows =
            nextState === 'check' ? 'Check' : nextState === 'uncheck' ? 'Uncheck' : 'Intermediate';
        const selection = getReportsSelectionModule();
        if (selection) {
            selection.isHdrSelectAllClicked = nextState === 'check';
        }
    };

    const applyPersistedSelection = (records: ReportTableRow[]) => {
        const grid = gridRef.current;
        const selection = getReportsSelectionModule();
        if (!grid || !selection) {
            return;
        }
        syncSelectedRowIds(records);
        selection.persistSelectedData = records.slice();
        selection.selectedRowState = Object.fromEntries(records.map((report) => [String(report.id), true]));
        selection.isHdrSelectAllClicked = records.length > 0;
        (grid as { checkAllRows?: string }).checkAllRows = records.length > 0 ? 'Check' : 'Uncheck';
        if (records.length === 0) {
            grid.clearSelection();
            return;
        }
        const viewIds = new Set(((grid.getCurrentViewRecords() ?? []) as ReportTableRow[]).map((report) => report.id));
        const indexes = records
            .filter((report) => report.id != null && viewIds.has(report.id))
            .map((report) => grid.getRowIndexByPrimaryKey(report.id as number))
            .filter((index): index is number => typeof index === 'number' && index >= 0);
        if (indexes.length > 0) {
            grid.selectRows(indexes);
        }
    };

    const handleHeaderCheckboxToggle = (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const headerCheckboxCell = target.closest('th.e-headercell');
        if (!headerCheckboxCell?.querySelector('.e-headerchkcelldiv')) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const selectedCount = selectAllActiveRef.current ? getSelectableReports().length : getSelectedRowIdsInScope().size;
        const nextRecords = selectedCount > 0 ? [] : getSelectableReports();
        selectAllActiveRef.current = nextRecords.length > 0;
        applyPersistedSelection(nextRecords);
        syncHeaderCheckboxToSelection();
        notifySelectionChanged();
    };
    handleHeaderCheckboxToggleRef.current = handleHeaderCheckboxToggle;

    const restoreSelectAllIfNeeded = () => {
        if (!selectAllActiveRef.current) {
            return;
        }
        const selectable = getSelectableReports();
        const persistCount = getReportsSelectionModule()?.persistSelectedData?.length ?? 0;
        if (persistCount !== selectable.length || selectedRowIdsRef.current.size !== selectable.length) {
            applyPersistedSelection(selectable);
        }
    };

    const handleRowSelected = (args: RowSelectEventArgs) => {
        if (bulkEditSession.suppressSelectionSync) {
            return;
        }
        if (args.isHeaderCheckboxClicked) {
            // Select-all replaces the set from the full filtered/unfiltered data set — not
            // args.data, which can be truncated under virtualization.
            selectedRowIdsRef.current = new Set(getSelectAllRowIds());
        } else {
            getIdsFromRowEventData(args.data).forEach((id) => selectedRowIdsRef.current.add(id));
        }
        if (selectAllActiveRef.current) {
            restoreSelectAllIfNeeded();
            syncHeaderCheckboxToSelection();
            notifySelectionChanged();
            return;
        }
        syncHeaderCheckboxToSelection();
        notifySelectionChanged();
    };

    const handleRowDeselected = (args: RowDeselectEventArgs) => {
        if (bulkEditSession.suppressSelectionSync) {
            return;
        }
        if (args.isHeaderCheckboxClicked) {
            // Header uncheck clears persistSelectedData before Syncfusion builds the event, so
            // args.data is often empty with persistSelection + virtualization. Clear our set.
            selectedRowIdsRef.current.clear();
        } else {
            getIdsFromRowEventData(args.data).forEach((id) => selectedRowIdsRef.current.delete(id));
            // After a virtualized select-all, rows that never received a deselect event can linger in
            // our set. When the header checkbox is fully unchecked, trust that and drop leftovers.
            if (isHeaderCheckboxUnchecked()) {
                selectedRowIdsRef.current.clear();
            }
        }
        syncHeaderCheckboxToSelection();
        notifySelectionChanged();
    };

    // Fires after header (and row) checkbox actions complete. Authoritative for header uncheck after
    // bulk-edit cancel restores selection via selectRows -- that path leaves the header in an
    // Intermediate state where rowDeselected alone may not clear selectedRowIdsRef. On header check,
    // re-apply the full filtered/unfiltered id set (same source as handleRowSelected) so we never
    // fall back to getSelectedRecords().
    const handleCheckBoxChange = (args: CheckBoxChangeEventArgs) => {
        if (bulkEditSession.suppressSelectionSync) {
            return;
        }
        if (!args.target?.classList?.contains('e-checkselectall')) {
            return;
        }
        if (!args.checked || !args.selectedRowIndexes?.length) {
            selectedRowIdsRef.current.clear();
        } else {
            selectedRowIdsRef.current = new Set(getSelectAllRowIds());
        }
        notifySelectionChanged();
    };

    const checkForOutdatedReports = () => {
        const executingReports = gridRef.current
            ?.getCurrentViewRecords()
            .filter((item: any) => item.executed === t('calculating').toString()) as Report[];

        executingReports?.forEach((executingReport) => {
            const outdatedReport = dataManager.dataSource.json?.find((item: Report) => item.id === executingReport.id) as Report;
            if (outdatedReport.id) {
                gridRef.current?.setRowData(outdatedReport.id, outdatedReport);
            }
        });
    };

    const getReportsTablePerformanceInfo = () => ({
        rowCount: contentLength,
        columnCount: numberOfColumns,
        visibleColumnCount: numberOfColumns,
    });

    /**
     * Re-applies the Add New button's click-tracking attribute.
     *
     * Syncfusion's `ItemModel.htmlAttributes` puts the attribute on the wrapping `.e-toolbar-item` div,
     * which `useGlobalClickTracker` never reads: it resolves the tracked name from
     * `closest('button, a, li[role="menuitem"]')`, and that returns the inner `<button>`. So the attribute
     * has to be set on the rendered button element itself. Called from both `created` and `dataBound`
     * because Syncfusion re-renders the toolbar whenever the `toolbar` item collection changes
     * (e.g. once functional access resolves and the item's `disabled` state flips).
     */
    // Syncfusion's ItemModel.disabled only adds .e-overlay to the toolbar item wrapper; the inner <button> keeps
    // aria-disabled="false", so screen readers announce a disabled Save as available. Mirror the class onto it.
    const applySaveButtonAriaDisabled = () => {
        const saveButton = gridRef.current?.element?.querySelector<HTMLElement>(`#${BULK_EDIT_SAVE_TOOLBAR_ITEM_ID}`);
        if (saveButton) {
            saveButton.setAttribute('aria-disabled', String(!!saveButton.closest('.e-toolbar-item')?.classList.contains('e-overlay')));
        }
    };

    const applyAddNewReturnAnalyticsAttribute = () => {
        gridRef.current?.element
            ?.querySelector(`#${ADD_NEW_RETURN_TOOLBAR_ITEM_ID}`)
            ?.setAttribute(DATA_ATTR_ANALYTICS_BUTTON_NAME, ADD_NEW_RETURN_ANALYTICS_NAME);
    };

    // Rows outside the virtualized window are not reachable via selectRows (no index yet), and rows hidden by the
    // restored search/filter are not in the filtered scope. Syncfusion paints their checkbox/aria-selected from its
    // persisted state when they render, so seed that state (and our tracked selection) from every selectable
    // report in `ids`, not just the visible ones; otherwise they come back unchecked after the filter clears.
    const seedPersistedSelection = (ids: Set<number>) => {
        selectedRowIdsRef.current = new Set(ids);
        const selection = getReportsSelectionModule();
        if (selection) {
            const selectedRecords = reportRows.filter((row) => isReportSelectable(row) && ids.has(row.id as number));
            selection.persistSelectedData = selectedRecords.slice();
            selection.selectedRowState = Object.fromEntries(selectedRecords.map((report) => [String(report.id), true]));
        }
    };

    const restoreGridSelectionFromIds = (ids: Set<number>) => {
        const grid = gridRef.current;
        if (!grid || ids.size === 0) {
            return;
        }
        // Full selectable set must be persisted so rows outside the virtual viewport stay checked.
        // Header appearance is painted later from selectedRowIdsRef / selectAllActiveRef.
        const selectable = getSelectableReports();
        const isFullSelectableSelection = selectable.length > 0 && selectable.every((row) => row.id != null && ids.has(row.id));
        if (isFullSelectableSelection) {
            applyPersistedSelection(selectable);
            // Rows the restored search/filter hides are still selected; keep them for when it is cleared.
            seedPersistedSelection(ids);
            return;
        }
        seedPersistedSelection(ids);
        const indexes: number[] = [];
        ids.forEach((id) => {
            const index = grid.getRowIndexByPrimaryKey(id);
            if (typeof index === 'number' && index >= 0) {
                indexes.push(index);
            }
        });
        if (indexes.length > 0) {
            grid.selectRows(indexes);
        }
    };
    // Re-selecting rows after leaving bulk edit makes Syncfusion move focus into the grid body (first immediately,
    // then again once virtualized rows re-render), stealing it from the Bulk edit button the exit focused.
    // Put it back, but only reclaim focus from the grid content so a user who has moved elsewhere is not overridden.
    // The reclaim also stops as soon as the user presses a key or pointer, so it never fights a deliberate move.
    const keepFocusOnBulkEditButtonAfterExit = () => {
        stopFocusReclaimRef.current?.();
        const reclaimFocus = (onlyFromGridContent: boolean) => {
            const gridElement = gridRef.current?.element;
            const bulkEditButton = gridElement?.querySelector<HTMLElement>(`#${BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID}`);
            if (!gridElement || !bulkEditButton || document.activeElement === bulkEditButton) {
                return;
            }
            const active = document.activeElement;
            if (!onlyFromGridContent || (active && active !== document.body && gridElement.querySelector('.e-content')?.contains(active))) {
                bulkEditButton.focus();
            }
        };
        reclaimFocus(false);
        const delays = [100, 400, 1000];
        const timers = delays.map((delay, index) =>
            window.setTimeout(() => {
                reclaimFocus(true);
                if (index === delays.length - 1) {
                    stopReclaim();
                }
            }, delay)
        );
        function stopReclaim() {
            timers.forEach((timer) => window.clearTimeout(timer));
            document.removeEventListener('pointerdown', stopReclaim, true);
            document.removeEventListener('keydown', stopReclaim, true);
            stopFocusReclaimRef.current = undefined;
        }
        document.addEventListener('pointerdown', stopReclaim, true);
        document.addEventListener('keydown', stopReclaim, true);
        stopFocusReclaimRef.current = stopReclaim;
    };

    const onDatabound = () => {
        // This is called after any updates to the table data
        checkForOutdatedReports();
        trackScrollFinished(getReportsTablePerformanceInfo());
        applyAddNewReturnAnalyticsAttribute();
        applySaveButtonAriaDisabled();
        restoreSelectAllIfNeeded();
        // Also flushed here (not just from the toolbarItems effect above) since toolbarModule only exists
        // once Syncfusion's own dataBound has fired -- on first mount, the toolbarItems effect can otherwise
        // race ahead of that and silently no-op.
        scheduleBulkEditToolbarItemStateUpdate();
        // rowDataBound for every row in this pass has already run and consumed the flag if it was set --
        // clear it so unrelated future binds (sort, scroll, filter) don't also animate.
        bulkEditSession.setAnimateNextRowBind(false);

        // Re-apply the entry-view snapshot on the remounted browse host (Amendment B). dataBind()
        // triggers a second dataBound where selection restore runs against the restored row set.
        if (!bulkEditSession.isActive && pendingBrowseViewSnapshotRef.current) {
            const rebound = applyPendingBrowseViewSnapshot();
            if (rebound) {
                queueMicrotask(() => syncHeaderCheckboxToSelection());
                return;
            }
        }

        let headerPaintAfterRestore = false;
        if (bulkEditSession.restoreSelectionAfterExit && !bulkEditSession.isActive) {
            const idsToRestore = bulkEditSession.takeRestoreSelection();
            if (idsToRestore) {
                headerPaintAfterRestore = true;
                // Defer one frame so the restored full-list rows (and their checkboxes) exist before we select.
                // Paint the header after that, once Syncfusion's setCheckAllState has run.
                requestAnimationFrame(() => {
                    restoreGridSelectionFromIds(idsToRestore);
                    bulkEditSession.clearSuppressSelectionSync();
                    notifySelectionChanged();
                    queueMicrotask(() => syncHeaderCheckboxToSelection());
                    keepFocusOnBulkEditButtonAfterExit();
                });
            }
        }

        if (!headerPaintAfterRestore) {
            queueMicrotask(() => syncHeaderCheckboxToSelection());
        }

        if (selectedCellIndex) {
            // Timeout needed due to virtualization re-render
            setTimeout(() => {
                if (selectedCellIndex?.rowIndex !== undefined && gridRef.current) {
                    focusOnSelectedCell(false);
                    setSelectedCellIndex(undefined);
                }
            }, 100);
        }
    };

    const applyPendingBrowseViewSnapshot = (): boolean => {
        const snapshot = pendingBrowseViewSnapshotRef.current;
        pendingBrowseViewSnapshotRef.current = null;
        const grid = gridRef.current;
        if (!snapshot || !grid) {
            return false;
        }
        grid.searchSettings.key = snapshot.searchKey;
        // Each snapshot predicate carries the `uid` of a column on the (now destroyed) bulk-edit-entry grid.
        // Syncfusion resolves predicates back to columns by that uid (addFilteredClass, clearFiltering), and
        // the remounted grid's columns have new uids -- the lookup returns undefined and throws, leaving the
        // filter icon unpainted and Clear Filters broken. Re-point each predicate at the new grid's column.
        // Predicates whose column no longer exists are dropped rather than left pointing at a wrong header.
        grid.filterSettings.columns = (snapshot.filterColumns as PredicateModel[]).flatMap((predicate) => {
            const column = predicate.field ? grid.getColumnByField(predicate.field) : undefined;
            return column ? [{ ...predicate, uid: column.uid }] : [];
        });
        setHasActiveColumnFilters(snapshot.filterColumns.length > 0);
        const restoredSortColumns = cloneSortColumns(snapshot.sortColumns);
        try {
            if (!areSortColumnsEqual(grid.sortSettings.columns, restoredSortColumns)) {
                // Cancel remounts the browse grid, which reapplies the default report-name sort before
                // this restore runs. Assigning sortSettings.columns leaves that name indicator up, and
                // the fresh sort module has no direction, so the shared comparer no-ops and name order
                // sticks. sortColumn clears the previous indicator and re-sorts with the browse direction.
                clearHeaderSortIndicators(grid);
                const primaryDirection = restoredSortColumns[restoredSortColumns.length - 1]?.direction;
                if (primaryDirection) {
                    sortingDirection.current = primaryDirection;
                }
                if (restoredSortColumns.length === 0) {
                    grid.clearSorting();
                } else {
                    restoredSortColumns.forEach((column, index) => {
                        grid.sortColumn(column.field, column.direction, index > 0);
                    });
                }
            } else {
                grid.dataBind();
            }
            return true;
        } catch {
            return false;
        }
    };

    const onCreated = () => {
        trackRenderFinished(getReportsTablePerformanceInfo());
        applyAddNewReturnAnalyticsAttribute();
        applySaveButtonAriaDisabled();
        if (focusBulkEditOnBrowseCreateRef.current) {
            focusBulkEditOnBrowseCreateRef.current = false;
            const bulkEditButton = gridRef.current?.element?.querySelector<HTMLElement>(`#${BULK_EDIT_RETURNS_TOOLBAR_ITEM_ID}`);
            bulkEditButton?.focus();
        }

        // #229 select-all: capture-phase header/row checkbox handling. Cell hit-area forwarding lives in
        // ReportsBrowseGrid.handleCreated (avoid double-binding the bubble forwarder here).
        headerCheckboxClickCleanupRef.current?.();
        const gridElement = gridRef.current?.element;
        if (!gridElement) {
            return;
        }
        const onHeaderClick = (event: Event) => {
            const target = event.target;
            if (target instanceof Element && target.closest('.e-rowcell.e-gridchkbox') && !target.closest('th')) {
                selectAllActiveRef.current = false;
            }
            handleHeaderCheckboxToggleRef.current(event);
        };
        gridElement.addEventListener('click', onHeaderClick, true);
        headerCheckboxClickCleanupRef.current = () => {
            gridElement.removeEventListener('click', onHeaderClick, true);
        };
    };
    const onActionBegin = (args: GridActionEventArgs) => {
        if (args.requestType === 'virtualscroll') {
            trackScrollStarted();
        }
        handleActionBegin(args);
    };

    const closeAddReturnPanelAndRefresh = (createdTaxReturnItemKeys: number[]) => {
        clearSearch();
        clearSearchBar(gridRef);

        if (isEditCalculationEnhancementsEnabled) {
            queryClient.invalidateQueries(getTaxReturnsNameListQueryKey);
            disableDefaultSorting();
            gridRef.current?.sortModule.clearSorting();
            gridRef.current?.clearFiltering();
            setRecentlyCreatedTaxReturnItemKeys(createdTaxReturnItemKeys);
            clearAllFilters();
        } else {
            getTaxReturns({ data: memoizedFilters });
        }

        closeAddReturnPanel();
    };

    const updateRowInPlaceAfterEditing = async (item: ShowableTaxReturnItemRecord) => {
        const shouldClearExecutedData = item.contextKey === EMPTY_CONTEXT_KEY_VALUE;

        const updatedReturn = await refetchReport();
        if (updatedReturn.data) {
            const taxReturn = updatedReturn.data;
            if (shouldClearExecutedData) {
                removeTaxReturnCalculationReport(taxReturn.id!);
                removeReturn(taxReturn.id!);
                taxReturn.executed = undefined;
                taxReturn.userFullName = undefined;
            }
            updateTaxReturnRowDataInPlace(taxReturn?.id || 0, taxReturn);
        }
    };

    const onSubmitEditReturn = async (editedData: ShowableTaxReturnItemRecord | undefined) => {
        if (editedData) {
            const request: TaxReturnSaveRecordsRequest = { taxReturnItemRecords: [editedData] };

            const saveResponse = await updateTaxReturns({ data: request });
            if (!saveResponse.hasErrors) {
                displayFeedbackMessage({ type: 'success', message: t('calcEdited') });
                await updateRowInPlaceAfterEditing(editedData);
            } else {
                displayFeedbackMessage({ type: 'error', message: t('calcNotEdited') });
            }

            setSelectedReturn(undefined);
        }
    };

    const renderDisabledCheckbox = () => <Checkbox disabled indicator={{ className: styles.disabledCheckboxIndicator }} />;

    const getCheckboxColumnTemplate = () => (!functionalAccess?.viewTaxReturnsEnabled ? renderDisabledCheckbox() : undefined);

    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        if (args.data && !functionalAccess?.viewTaxReturnsEnabled && args.row) {
            args.isSelectable = false;

            const checkBoxElement = args.row.querySelector('.e-checkbox-wrapper') as HTMLElement;
            if (checkBoxElement) {
                checkBoxElement.style.display = 'none';
                const checkboxelement = checkBoxElement.firstChild as HTMLElement;
                checkboxelement.remove();
            }
        }

        if (bulkEditSession.animateNextRowBind && args.row) {
            const row = args.row;
            // Virtualization can recycle an already-visible <tr> for this new data rather than creating a
            // fresh one -- setting the hidden state synchronously here (before this row's new content is
            // ever painted) means the very first frame already shows it, so there's no flash of stale
            // content before the fade/slide-in kicks in. Only flip to visible on the next frame, once
            // that hidden state (and the transition above) have actually been committed.
            addClassNames(row, styles.rowEnterTransition, styles.rowHidden);
            requestAnimationFrame(() => {
                removeClassNames(row, styles.rowHidden);
            });
        }
    };

    const columnChooserSettings = { operator: 'contains' };
    const gridHeight = contentLength === 0 || !hasMatch ? '42px' : '100%';
    const columnsProps: Omit<IReportsTableColumnsProps, 'templates' | 'mode'> = {
        isColumnVisible,
        reportHeaderTemplate,
        getColumnTemplate,
        getBooleanColumnTemplate,
        statusFilterItemTemplate,
        sortComparer,
        t,
    };
    const browseTemplates: IReportsTableTemplates = {
        warningIcon: getCheckboxColumnTemplate,
        reportName: reportNameTemplateTruncated,
        executed: getExecutedTemplate,
        status: reportStatusTemplate,
    };
    const editTemplates: IReportsTableTemplates = {
        warningIcon: getCheckboxColumnTemplate,
        reportName: (item) => <BulkEditReportNameCell item={item} viewTaxReturnsEnabled={functionalAccess?.viewTaxReturnsEnabled} />,
        executed: (item) => <LastExecutedReportLink {...item} disabled />,
        status: bulkEditStatusTemplate,
    };
    const sharedGridProps = {
        gridRef,
        columnsProps,
        height: gridHeight,
        toolbarItems,
        onToolbarClick,
        sortSettings: defaultSortingEnabled ? sortingOptions : undefined,
        pageSettings,
        searchSettings: searchSetting,
        filterSettings,
        columnChooserSettings,
        selectionSettings,
        onCreated,
        onDataBound: onDatabound,
        onActionBegin,
        onActionComplete: handleActionComplete,
        onQueryCellInfo: queryCellInfo,
        onRowDataBound: handleRowDataBound,
        onBeforeOpenColumnChooser: handleBeforeOpenColumnChooser,
    };

    return (
        <GridStateContextProvider grid={gridRef}>
            <ReturnDetailsContextProvider>
                <TaxReturnCalculationProvider>
                    <div className={wrapperStyles}>
                        <div className={filterBarStyles}>
                            <FilterBar
                                updateFolderList={updateFolderList}
                                disableActionButtons={disableActionButtons}
                                folders={folders}
                                disabled={isLoading}
                                isBulkEditActive={bulkEditSession.isActive || bulkEditSession.isEntering}
                                getReportsToExport={handleReportToExport}
                            />
                        </div>
                        {isLoading || !hasLatestCalculationStatus ? (
                            <div className={loadingSpinnerContainer}>
                                <LoadingSpinner
                                    data-testid='loadingSpinner'
                                    id='loadingSpinner'
                                    label={t('loadingPleaseWait').toString()}
                                />
                            </div>
                        ) : (
                            <div
                                className={mergeClasses(
                                    scrollablePaneWrapperStyles,
                                    bulkEditSession.isEntering && styles.swapLocked,
                                    bulkEditSession.isActive && bulkEditActive
                                )}
                            >
                                <ReportsGridDialogGlobalStyles />
                                {bulkEditSession.isActive ? (
                                    <ReportsBulkEditGrid
                                        {...sharedGridProps}
                                        editViewRows={bulkEditSession.editViewRows}
                                        templates={editTemplates}
                                    />
                                ) : (
                                    <ReportsBrowseGrid
                                        {...sharedGridProps}
                                        dataManager={dataManager}
                                        templates={browseTemplates}
                                        tableKey={tableKey}
                                        onRowSelected={handleRowSelected}
                                        onRowDeselected={handleRowDeselected}
                                        onCheckBoxChange={handleCheckBoxChange}
                                    />
                                )}
                                <AddReturnPanel
                                    isPanelOpen={isAddReturnPanelOpen}
                                    handleClose={closeAddReturnPanel}
                                    onSuccess={closeAddReturnPanelAndRefresh}
                                    allReturnsNames={allReturnsNames ?? []}
                                />
                            </div>
                        )}
                    </div>
                    <EditReturnModal
                        isOpen={isEditReturnModalOpen}
                        selectedReturn={selectedReturn}
                        onClose={closeEditReturnModal}
                        onSubmit={onSubmitEditReturn}
                        onDismissed={() => focusOnSelectedCell(false)}
                        allReturnsNames={allReturnsNames ?? []}
                        hasEditTaxReturnsDataEnabled={functionalAccess?.editTaxReturnsDataEnabled}
                    />
                    <ConfirmReturnDeletionModal
                        isModalVisible={isDeleteReturnModalOpen}
                        onConfirm={(returnKey, response) => {
                            closeDeleteReturnModal();
                            if (!response.hasErrors) {
                                displayFeedbackMessage({ type: 'success', message: t('calcDeleted') });
                                deleteTaxReturnRowDataInPlace(returnKey);
                                queryClient.invalidateQueries(getTaxReturnsNameListQueryKey);
                            }
                            if (response.hasErrors) {
                                displayFeedbackMessage({ type: 'error', message: t('calcNotDeleted') });
                            }
                        }}
                        onCancel={closeDeleteReturnModal}
                        returnKey={returnToDelete}
                    />
                </TaxReturnCalculationProvider>
            </ReturnDetailsContextProvider>
        </GridStateContextProvider>
    );
};

export const handleDisableActionButtons = (selectedRecords: unknown[] | undefined, setDisableActionButtons: (value: boolean) => void) => {
    setDisableActionButtons(!selectedRecords || selectedRecords.length === 0);
};

export default withAITracking(applicationInsightsReactPlugin, ReportsTable, 'List of returns');
